import createClient from 'openapi-fetch';
import { getSessionToken } from './better-auth';
import { isNativePlatform } from './native-features';
import type {
  // Keep manual types for entities that don't have OpenAPI routes yet or need custom handling
  AssetOpenApi,
  AssetParameter,
  AssetParameterValue,
  AssetPart,
  AssetPartType,
  AssetUpdateOpenApi,
  AuthResponse,
  BadgeDefinition,
  CalendarEvent,
  CertificateAttachment,
  ChecklistAttachment,
  ChecklistFieldResponse,
  ChecklistInstance,
  ChecklistTemplate,
  ChecklistTemplateField,
  ChecklistTemplateFieldOption,
  ChecklistTemplateSection,
  ChecklistTemplateStep,
  Comment,
  CreateGroupRequest,
  CreateLocationRequest,
  CreateMaintenanceTicketRequest,
  CreatePartCategoryRequest,
  CreatePartItemRequest,
  CreatePartTypeRequest,
  CreatePartUnitRequest,
  CreateRoleRequest,
  CreateShiftRequest,
  CreateSupplierRequest,
  CreateToolCalibrationRequest,
  CreateToolCategoryRequest,
  CreateToolModelRequest,
  CreateToolRequest,
  EffortLevel,
  GamificationProfile,
  Group,
  GroupApi,
  InstalledPart,
  KnowledgeDoc,
  KnowledgeFolder,
  KnowledgeFolderTreeNode,
  LevelDefinition,
  LocationApi,
  LoginCredentials,
  Machine,
  MaintenanceOccurrence,
  MaintenanceTicket,
  MaintenanceTicketApi,
  MaintenanceTicketTransaction,
  NewAsset,
  NewAssetParameter,
  NewAssetParameterValue,
  NewAssetPart,
  NewChecklistFieldResponse,
  NewChecklistInstance,
  NewChecklistTemplate,
  NewChecklistTemplateField,
  NewChecklistTemplateFieldOption,
  NewChecklistTemplateSection,
  NewChecklistTemplateStep,
  NewComment,
  NewEffortLevel,
  NewGroup,
  NewInstalledPart,
  NewKnowledgeDoc,
  NewKnowledgeFolder,
  NewMachine,
  NewMaintenanceOccurrence,
  NewMaintenanceTicket,
  NewMaintenanceTicketTransaction,
  NewParameter,
  // Location, // imported via alias below
  // Tool, // imported via alias below
  // ToolCategory, // imported via alias below
  // PartCategory, // imported via alias below
  NewPartCategory,
  NewPartItem,
  NewPartItemBatch,
  NewPartItemBatchTransaction,
  NewPartItemCheckout,
  // PartUnit, // imported via alias below
  NewPartUnit,
  NewPreventiveMaintenanceSchedule,
  NewProblem,
  NewProblemAction,
  NewProblemAsset,
  NewProblemAssetSubAction,
  NewProblemGroup,
  NewProblemSubAction,
  NewRole,
  NewShift,
  NewShiftPattern,
  NewShiftPatternDetail,
  NewShiftRoster,
  NewShiftSlot,
  // Supplier, // imported via alias below
  NewSupplier,
  NewSymptom,
  NewSymptomParameterLink,
  NewSymptomProblemLink,
  NewTicketPriority,
  NewTicketRule,
  NewTicketStatusRecord,
  NewTicketRuleExecution,
  NewTicketSymptom,
  NewToolTransaction,
  NewUser,
  NewUserGroup,
  NewUserRole,
  Parameter,
  PartCategoryApi,
  PartItem,
  PartItemApi,
  PartItemBatch,
  PartItemBatchTransaction,
  PartItemCheckout,
  PartTypeApi,
  PartUnitApi,
  Permission,
  PointsEvent,
  PreventiveMaintenanceSchedule,
  Problem,
  ProblemAction,
  ProblemAsset,
  ProblemAssetSubAction,
  ProblemGroup,
  ProblemSubAction,
  Qualification,
  RegisterCredentials,
  Role,
  RoleApi,
  RuleCondition,
  RuleDefinition,
  Shift,
  ShiftApi,
  ShiftPattern,
  ShiftPatternDetail,
  ShiftRoster,
  ShiftSlot,
  SkillCoverage,
  // Generated types from OpenAPI spec (with Api suffix to distinguish from manual schema types)
  SupplierApi,
  Symptom,
  SymptomParameterLink,
  SymptomProblemLink,
  TicketPriority,
  TicketRule,
  TicketStatusRecord,
  TicketRuleExecution,
  TicketSymptom,
  ToolApi,
  ToolCalibrationApi,
  ToolCategoryApi,
  ToolModelApi,
  ToolTransaction,
  Training,
  TrainingSettings,
  UpdateGroupRequest,
  UpdateLocationRequest,
  UpdateMaintenanceTicketRequest,
  UpdatePartCategoryRequest,
  UpdatePartItemRequest,
  UpdatePartTypeRequest,
  UpdatePartUnitRequest,
  UpdateRoleRequest,
  UpdateShiftRequest,
  UpdateSupplierRequest,
  UpdateToolCalibrationRequest,
  UpdateToolCategoryRequest,
  UpdateToolModelRequest,
  UpdateToolRequest,
  User,
  UserGroup,
  Competition,
  CompetenceProfile,
  UserQualification,
  UserRole,
  UserTraining,
  // v1.50.0 — Inventory Optimization & Procurement
  AssetBomItem,
  InventoryOptimizationDashboard,
  NewAssetBomItem,
  NewWarehouse,
  NewWarehouseLocation,
  NewPurchaseOrder,
  PartWarehouseStock,
  PurchaseOrder,
  PurchaseOrderStatus,
  SupplierPerformanceDashboard,
  SupplierPerformanceMetric,
  Warehouse,
  WarehouseLocation,
} from 'shared';

interface UserWithQualifications extends User {
  qualifications?: UserQualification[];
}

// Storage key for backend URL
const BACKEND_URL_KEY = 'smas_backend_url';

// Get backend URL from localStorage or use default
function getBackendUrl(): string {
  const stored = localStorage.getItem(BACKEND_URL_KEY);
  if (stored?.trim()) {
    // Remove trailing slash and ensure /api suffix
    const url = stored.trim().replace(/\/$/, '');
    return url.endsWith('/api') ? url : `${url}/api`;
  }
  // Use env variable if available (works for both dev and production/mobile)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // In production web, use the current origin (frontend URL as base)
  return typeof window !== 'undefined' ? window.location.origin : '/api';
}

// Set backend URL in localStorage
export function setBackendUrl(url: string) {
  localStorage.setItem(BACKEND_URL_KEY, url);
}

// Get current backend URL
export function getBackendUrlSetting(): string {
  return localStorage.getItem(BACKEND_URL_KEY) || '';
}

// Clear backend URL setting
export function clearBackendUrl() {
  localStorage.removeItem(BACKEND_URL_KEY);
}

// ============================================================================
// OPENAPI FETCH CLIENT (E2E Type Safety)
// ============================================================================

/**
 * Import types from the auto-generated OpenAPI schema.
 *
 * Run `bun run generate:types` to regenerate these types from the server's OpenAPI spec.
 */
import type { paths } from '../types/api-schema';

/**
 * Type-safe API client using openapi-fetch.
 *
 * This client provides full end-to-end type safety:
 * - Request bodies are validated against the OpenAPI schema
 * - Response types are inferred from the OpenAPI schema
 * - Paths and parameters are autocompleted by TypeScript
 *
 * @example
 * ```ts
 * // GET request with query parameters
 * const { data } = await apiClient.GET('/suppliers', {
 *   params: { query: { page: 1, limit: 10 } }
 * })
 *
 * // POST request with body
 * const { data } = await apiClient.POST('/suppliers', {
 *   body: { name: 'Acme Corp', email: 'contact@acme.com' }
 * })
 * ```
 */
export const apiClient = createClient<paths>({
  baseUrl: '', // Empty string since we use getBackendUrl() dynamically
  headers: {
    'Content-Type': 'application/json',
  },
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    // Build full URL with backend URL
    const baseUrl = getBackendUrl();

    // Handle both string URLs and Request objects
    let url: string | URL;
    if (typeof input === 'string') {
      url = input.startsWith('/') ? `${baseUrl.replace(/\/api$/, '')}${input}` : input;
    } else if (input instanceof URL) {
      url = input.toString().startsWith('/')
        ? `${baseUrl.replace(/\/api$/, '')}${input.toString()}`
        : input;
    } else {
      // input is a Request object - get its URL and prepend baseUrl if needed
      const requestUrl = input.url.toString();
      url = requestUrl.startsWith('/')
        ? `${baseUrl.replace(/\/api$/, '')}${requestUrl}`
        : requestUrl;
    }

    // Use native fetch with credentials for Better Auth cookies
    // Also inject Bearer token for Capacitor/mobile where cookies don't work cross-origin
    // Convert to plain object — CapacitorHttp may not serialize Headers instances properly
    const headerObj: Record<string, string> = {};
    if (init?.headers) {
      const initHeaders = init.headers instanceof Headers
        ? Object.fromEntries(init.headers.entries())
        : (init.headers as Record<string, string>);
      for (const [key, val] of Object.entries(initHeaders)) {
        headerObj[key] = val;
      }
    }
    const sessionToken = getSessionToken();
    if (sessionToken && sessionToken !== 'undefined' && !headerObj['Authorization']) {
      headerObj['Authorization'] = `Bearer ${sessionToken}`;
    }

    return fetch(url, {
      ...init,
      headers: headerObj,
      credentials: 'include',
    });
  },
});

/**
 * Helper type for extracting the response data type from an openapi-fetch call.
 * This handles the { data, error, response } return type.
 */
export type ExtractData<T> =
  T extends Promise<infer U> ? (U extends { data?: infer D } ? D : never) : never;

/**
 * Helper type for extracting the error type from an openapi-fetch call.
 */
export type ExtractError<T> =
  T extends Promise<infer U> ? (U extends { error?: infer E } ? E : never) : never;

/**
 * Handle API response from openapi-fetch, throwing on errors.
 *
 * This converts the { data, error } response pattern to a Promise that resolves
 * to the data type or throws an ApiError.
 *
 * @example
 * ```ts
 * const suppliers = await handleApiResponse(
 *   apiClient.GET('/suppliers')
 * )
 * ```
 */
export async function handleApiResponse<T>(
  responsePromise: Promise<{
    data?: T;
    error?: { error?: string; message?: string };
    response: Response;
  }>,
): Promise<T> {
  const response = await responsePromise;

  if (response.error) {
    throw new ApiError(
      response.error.message || response.error.error || 'API request failed',
      response.response.status,
      response.error,
    );
  }

  if (!response.data) {
    throw new ApiError('No data returned from API', response.response.status);
  }

  return response.data;
}

/**
 * Get the current base URL for the API client (without /api suffix).
 * Useful for constructing full URLs when needed.
 */
export function getApiBaseUrl(): string {
  const url = getBackendUrl();
  return url.replace(/\/api$/, '');
}

// ============================================================================
// LEGACY API CLIENT (for backward compatibility)
// ============================================================================

/**
 * Type-safe API client using shared types between server and client
 *
 * This uses a traditional fetch-based approach with strongly typed interfaces
 * from the shared package, providing excellent TypeScript inference and IDE support.
 *
 * @deprecated Prefer using `apiClient` from openapi-fetch for new code.
 * The legacy functions below will be maintained for backward compatibility.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public errors?: unknown,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set Content-Type if body is not FormData (browser sets it automatically for FormData)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // Inject Bearer token for Capacitor/mobile where cookies don't work cross-origin
  const sessionToken = getSessionToken();
  if (sessionToken && sessionToken !== 'undefined' && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  try {
    const baseUrl = getBackendUrl();
    const fullUrl = `${baseUrl}${endpoint}`;
    // console.log('[apiRequest] Making request:', { baseUrl, endpoint, fullUrl })
    const response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for Better Auth session
    });

    if (!response.ok) {
      // For 401 Unauthorized, redirect to login (better-auth will handle session validation)
      // Skip this for auth endpoints and if already on login/register page to prevent redirect loops
      // On native (Capacitor), don't hard-redirect — let the auth context handle it.
      // Hard redirects on native cause a full page reload which loses React state and device token.
      if (
        response.status === 401 &&
        !endpoint.startsWith('/auth/sign-in') &&
        !endpoint.startsWith('/auth/sign-up') &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/register') &&
        !isNativePlatform()
      ) {
        // Better-auth handles invalid sessions via cookies, just navigate to login
        window.location.href = '/login';
      }

      let errorData: {
        message?: string;
        error?: string;
        errors?: unknown;
        [key: string]: unknown;
      } = {};
      try {
        errorData = (await response.json()) as typeof errorData;
      } catch {
        errorData = { message: response.statusText };
      }

      throw new ApiError(
        errorData.message || errorData.error || response.statusText,
        response.status,
        errorData.errors,
        errorData,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error instanceof Error ? error.message : 'Network error occurred', 0);
  }
}

// Generic CRUD operations helpers
export async function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint);
}

export async function apiPost<T>(
  endpoint: string,
  data: unknown,
  options?: { headers?: Record<string, string> },
): Promise<T> {
  const isFormData = data instanceof FormData;
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: isFormData ? data : JSON.stringify(data),
    headers: options?.headers || {},
  });
}

export async function apiPatch<T>(endpoint: string, data: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function apiPut<T>(endpoint: string, data: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'DELETE',
  });
}

// ===== AUTH APIs =====
export const authApi = {
  login: (credentials: LoginCredentials) => apiPost<AuthResponse>('/auth/login', credentials),
  register: (credentials: RegisterCredentials) =>
    apiPost<AuthResponse>('/auth/register', credentials),
};

// ===== MACHINES API =====
export const machinesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<Machine[] | { data: Machine[]; totalPages?: number; totalItems?: number }>(
      `/machines${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Machine>(`/machines/${id}`),
  create: (data: NewMachine) => apiPost<Machine>('/machines', data),
  update: (id: string, data: Partial<NewMachine>) => apiPatch<Machine>(`/machines/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/machines/${id}`),
};

// ===== USERS API =====
export const usersApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    includeEffort?: boolean;
    prioritizeShift?: boolean;
    clientDate?: string; // YYYY-MM-DD (client's local date)
    clientTime?: string; // HH:mm (client's local time)
    includeQualifications?: boolean;
  }): Promise<
    | UserWithQualifications[]
    | {
        data: UserWithQualifications[];
        pagination: { page: number; limit: number; totalItems: number; totalPages: number };
      }
  > => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.includeEffort) searchParams.set('includeEffort', 'true');
    if (params?.prioritizeShift) searchParams.set('prioritizeShift', 'true');
    if (params?.clientDate) searchParams.set('clientDate', params.clientDate);
    if (params?.clientTime) searchParams.set('clientTime', params.clientTime);
    if (params?.includeQualifications) searchParams.set('includeQualifications', 'true');
    const query = searchParams.toString();
    const result = apiGet<
      | UserWithQualifications[]
      | {
          data: UserWithQualifications[];
          pagination: { page: number; limit: number; totalItems: number; totalPages: number };
        }
    >(`/users${query ? `?${query}` : ''}`);
    // For backward compatibility, if no pagination params and result is paginated, return just the data array
    if (!params?.page && !params?.limit) {
      return result.then((res) => {
        if (Array.isArray(res)) return res;
        return res.data;
      });
    }
    return result;
  },
  getById: (id: string) => apiGet<User>(`/users/${id}`),
  create: (data: NewUser) => apiPost<User>('/users', data),
  update: (id: string, data: Partial<NewUser>) => apiPatch<User>(`/users/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/users/${id}`),
  uploadProfilePicture: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<{ profilePictureUrl: string }>(`/users/${id}/profile-picture`, formData, {
      headers: {}, // Let browser set Content-Type with boundary for FormData
    });
  },
  subscribeToPush: (
    id: string,
    subscription: { endpoint: string; keys: { p256dh: string; auth: string } },
  ) => apiPost<{ message: string }>(`/users/${id}/push-subscription`, subscription),
  sendTestNotification: (id: string) =>
    apiPost<{ message: string }>(`/users/${id}/test-notification`, {
      title: 'Test Notification',
      body: 'This is a test push notification.',
    }),
  getVAPIDPublicKey: () => apiGet<{ publicKey: string }>('/users/vapid-public-key'),
  verifyPassword: (password: string) =>
    apiPost<{ success: boolean; message: string }>('/users/verify-password', { password }),
};

// ===== MAINTENANCE TICKET TRANSACTIONS API =====
export const maintenanceTicketTransactionsApi = {
  getByTicketId: (ticketId: string) =>
    apiGet<MaintenanceTicketTransaction[]>(`/maintenance-ticket-transactions?ticketId=${ticketId}`),
  create: (data: NewMaintenanceTicketTransaction) =>
    apiPost<MaintenanceTicketTransaction>('/maintenance-ticket-transactions', data),
};

// ===== MAINTENANCE TICKETS API =====
export const maintenanceTicketsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    search?: string;
    status?: string;
    priority?: string;
    type?: string;
    assetId?: string;
    assignedTo?: string;
    startDate?: string;
    endDate?: string;
    dateField?: 'createdAt' | 'scheduledAt';
    unassigned?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.assetId) searchParams.append('assetId', params.assetId);
    if (params?.assignedTo) searchParams.append('assignedTo', params.assignedTo);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.dateField) searchParams.append('dateField', params.dateField);
    if (params?.unassigned) searchParams.append('unassigned', 'true');
    const query = searchParams.toString();
    return apiGet<{
      data: MaintenanceTicket[];
      pagination: { page: number; limit: number; totalItems: number; totalPages: number };
    }>(`/maintenance-tickets${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<MaintenanceTicket>(`/maintenance-tickets/${id}`),
  create: (data: NewMaintenanceTicket) => apiPost<MaintenanceTicket>('/maintenance-tickets', data),
  update: (id: string, data: Partial<NewMaintenanceTicket>) =>
    apiPatch<MaintenanceTicket>(`/maintenance-tickets/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/maintenance-tickets/${id}`),
};

// ===== TICKET ASSETS API =====
export const ticketAssetsApi = {
  getAll: (ticketId: string) => apiGet<any[]>(`/ticket-assets/tickets/${ticketId}/assets`),
  create: (
    ticketId: string,
    data: {
      assetId: string;
      scopeType?: 'primary' | 'included_children' | 'explicit';
      isPrimary?: boolean;
      notes?: string;
    },
  ) => apiPost<any>(`/ticket-assets/tickets/${ticketId}/assets`, data),
  update: (
    ticketId: string,
    id: string,
    data: { scopeType?: string; isPrimary?: boolean; notes?: string },
  ) => apiPatch<any>(`/ticket-assets/tickets/${ticketId}/assets/${id}`, data),
  delete: (ticketId: string, id: string) =>
    apiDelete<void>(`/ticket-assets/tickets/${ticketId}/assets/${id}`),
};

// ===== SCHEDULE ASSETS API =====
export const scheduleAssetsApi = {
  getAll: (scheduleId: string) => apiGet<any[]>(`/schedule-assets/schedules/${scheduleId}/assets`),
  create: (scheduleId: string, data: { assetId: string; includeDescendants?: boolean }) =>
    apiPost<any>(`/schedule-assets/schedules/${scheduleId}/assets`, data),
  update: (scheduleId: string, id: string, data: { includeDescendants?: boolean }) =>
    apiPatch<any>(`/schedule-assets/schedules/${scheduleId}/assets/${id}`, data),
  delete: (scheduleId: string, id: string) =>
    apiDelete<void>(`/schedule-assets/schedules/${scheduleId}/assets/${id}`),
};

// ===== ROLES API =====
export const rolesApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: Role[]; totalPages?: number; totalItems?: number }>(
      `/roles${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Role>(`/roles/${id}`),
  create: (data: NewRole) => apiPost<Role>('/roles', data),
  update: (id: string, data: Partial<NewRole>) => apiPatch<Role>(`/roles/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/roles/${id}`),
};

// ===== GROUPS API =====
export const groupsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: Group[]; totalPages?: number; totalItems?: number }>(
      `/groups${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Group>(`/groups/${id}`),
  create: (data: NewGroup) => apiPost<Group>('/groups', data),
  update: (id: string, data: Partial<NewGroup>) => apiPatch<Group>(`/groups/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/groups/${id}`),
  getTree: () =>
    apiGet<{
      data: {
        sites: Array<{
          id: string;
          name: string;
          areas: Array<{
            id: string;
            name: string;
            groups: Array<{ id: string; name: string; color: string | null; userCount: number }>;
          }>;
          groups: Array<{ id: string; name: string; color: string | null; userCount: number }>;
        }>;
        unassignedGroups: Array<{
          id: string;
          name: string;
          color: string | null;
          userCount: number;
        }>;
      };
    }>('/groups/tree'),
};

// ===== SHIFTS API =====
export const shiftsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: Shift[]; totalPages?: number; totalItems?: number }>(
      `/shifts${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Shift>(`/shifts/${id}`),
  create: (data: NewShift) => apiPost<Shift>('/shifts', data),
  update: (id: string, data: Partial<NewShift>) => apiPatch<Shift>(`/shifts/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/shifts/${id}`),
};

// ===== SHIFT PATTERNS API =====
export const shiftPatternsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: ShiftPattern[]; totalPages?: number; totalItems?: number }>(
      `/shift-patterns${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) =>
    apiGet<ShiftPattern & { details?: ShiftPatternDetail[] }>(`/shift-patterns/${id}`),
  create: (data: NewShiftPattern & { details?: Omit<NewShiftPatternDetail, 'patternId'>[] }) =>
    apiPost<ShiftPattern>('/shift-patterns', data),
  update: (
    id: string,
    data: Partial<NewShiftPattern> & { details?: Omit<NewShiftPatternDetail, 'patternId'>[] },
  ) => apiPatch<ShiftPattern>(`/shift-patterns/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/shift-patterns/${id}`),
};

// ===== SHIFT ROSTERS API =====
export const shiftRostersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: ShiftRoster[]; totalPages?: number; totalItems?: number }>(
      `/shift-rosters${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ShiftRoster & { slots?: ShiftSlot[] }>(`/shift-rosters/${id}`),
  create: (data: NewShiftRoster) => apiPost<ShiftRoster>('/shift-rosters', data),
  update: (id: string, data: Partial<NewShiftRoster>) =>
    apiPatch<ShiftRoster>(`/shift-rosters/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/shift-rosters/${id}`),
  generate: (data: {
    startsOn: string;
    endsOn: string;
    patternId?: string | null;
    holidays?: string[];
    findFirstEmptyDay?: boolean;
  }) =>
    apiPost<{
      roster: ShiftRoster;
      slots: ShiftSlot[];
      summary: {
        totalSlots: number;
        totalUsers: number;
        dateRange: { start: string; end: string };
      };
    }>('/shift-rosters/generate', data),
};

// ===== SHIFT SLOTS API =====
export const shiftSlotsApi = {
  getAll: (params?: {
    rosterId?: string;
    userId?: string;
    groupId?: string;
    startDate?: string; // YYYY-MM-DD format
    endDate?: string; // YYYY-MM-DD format
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.rosterId) queryParams.append('rosterId', params.rosterId);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.groupId) queryParams.append('groupId', params.groupId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    const query = queryParams.toString();
    return apiGet<Array<ShiftSlot & { userName?: string }>>(
      `/shift-slots${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ShiftSlot>(`/shift-slots/${id}`),
  create: (data: NewShiftSlot) => apiPost<ShiftSlot>('/shift-slots', data),
  update: (id: string, data: Partial<NewShiftSlot>) =>
    apiPatch<ShiftSlot>(`/shift-slots/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/shift-slots/${id}`),
};

// ===== PARAMETERS API =====
export const parametersApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return apiGet<{ data: Parameter[]; totalPages?: number; totalItems?: number }>(
      `/parameters${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Parameter>(`/parameters/${id}`),
  create: (data: NewParameter) => apiPost<Parameter>('/parameters', data),
  update: (id: string, data: Partial<NewParameter>) =>
    apiPatch<Parameter>(`/parameters/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/parameters/${id}`),
};

// ===== USER ROLES API =====
export const userRolesApi = {
  getAll: (params?: { userId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.userId) queryParams.append('userId', params.userId);
    const query = queryParams.toString();
    return apiGet<UserRole[]>(`/user-roles${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<UserRole>(`/user-roles/${id}`),
  create: (data: NewUserRole) => apiPost<UserRole>('/user-roles', data),
  update: (id: string, data: Partial<NewUserRole>) => apiPatch<UserRole>(`/user-roles/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/user-roles/${id}`),
};

// ===== USER GROUPS API =====
export const userGroupsApi = {
  getAll: (params?: { userId?: string; groupId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.groupId) queryParams.append('groupId', params.groupId);
    const query = queryParams.toString();
    return apiGet<UserGroup[]>(`/user-groups${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<UserGroup>(`/user-groups/${id}`),
  create: (data: NewUserGroup) => apiPost<UserGroup>('/user-groups', data),
  update: (id: string, data: Partial<NewUserGroup>) =>
    apiPatch<UserGroup>(`/user-groups/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/user-groups/${id}`),
};

// ===== INVENTORY ITEMS API =====
export const partItemsApi = {
  getAll: () => apiGet<PartItem[]>('/part-items'),
  getById: (id: string) => apiGet<PartItem>(`/part-items/${id}`),
  create: (data: NewPartItem) => apiPost<PartItem>('/part-items', data),
  update: (id: string, data: Partial<NewPartItem>) => apiPatch<PartItem>(`/part-items/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/part-items/${id}`),
  bulkCreate: (
    items: Array<{
      name: string;
      code: string;
      partTypeId?: string | null;
      categoryId?: string | null;
      unitId?: string | null;
      supplierId?: string | null;
      locationId?: string | null;
      minStock?: number | null;
      maxStock?: number | null;
      cost?: number | null;
      description?: string | null;
    }>,
  ) =>
    apiPost<{
      success: boolean;
      created: number;
      failed: number;
      errors: Record<string, string[]>;
    }>('/part-items/bulk', { items }),
  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<{ imageUrl: string }>(`/part-items/${id}/image`, formData, {
      headers: {}, // Let browser set Content-Type with boundary for FormData
    });
  },
};

// ===== PART ITEMS API (OpenAPI pattern) =====
export const partItemsOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: PartItemApi[]; totalPages?: number; totalItems?: number }>(
      `/part-items${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartItemApi>(`/part-items/${id}`),
  create: (data: CreatePartItemRequest) => apiPost<PartItemApi>('/part-items', data),
  update: (id: string, data: UpdatePartItemRequest) =>
    apiPatch<PartItemApi>(`/part-items/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/part-items/${id}`),
};

// ===== LOCATIONS API =====
export const locationsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: Location[]; totalPages?: number; totalItems?: number }>(
      `/locations${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Location>(`/locations/${id}`),
  create: (data: { name: string; code: string; description?: string | null }) =>
    apiPost<Location>('/locations', data),
  update: (
    id: string,
    data: Partial<{ name: string; code: string; description?: string | null }>,
  ) => apiPatch<Location>(`/locations/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/locations/${id}`),
};

// ===== TOOLS API =====
export const toolsApi = {
  getAll: (params?: {
    toolModelId?: string;
    available?: boolean;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.toolModelId) queryParams.append('toolModelId', params.toolModelId);
    if (params?.available) queryParams.append('available', 'true');
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    const query = queryParams.toString();
    return apiGet<{ data: Tool[]; totalPages?: number; totalItems?: number }>(
      `/tools${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Tool>(`/tools/${id}`),
  create: (data: Partial<Tool> & { name: string; code: string }) => apiPost<Tool>('/tools', data),
  update: (id: string, data: Partial<Tool>) => apiPatch<Tool>(`/tools/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/tools/${id}`),
  bulkCreate: (
    items: Array<{
      name: string;
      code: string;
      toolModelId?: string | null;
      toolCategoryId?: string | null;
      unitId?: string | null;
      supplierId?: string | null;
      locationId?: string | null;
      minStock?: number | null;
      maxStock?: number | null;
      cost?: number | null;
      description?: string | null;
    }>,
  ) =>
    apiPost<{
      success: boolean;
      created: number;
      failed: number;
      errors: Record<string, string[]>;
    }>('/tools/bulk', { items }),
  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<{ imageUrl: string }>(`/tools/${id}/image`, formData, {
      headers: {}, // Let browser set Content-Type with boundary for FormData
    });
  },
};

// ===== TOOL CATEGORIES API =====
export const toolCategoriesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return apiGet<{ data: ToolCategory[]; totalPages?: number; totalItems?: number }>(
      `/tool-categories${query ? `?${query}` : ''}`,
    );
  },
  create: (data: { name: string; description?: string | null }) =>
    apiPost<ToolCategory>('/tool-categories', data),
  update: (id: string, data: Partial<{ name: string; description?: string | null }>) =>
    apiPatch<ToolCategory>(`/tool-categories/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/tool-categories/${id}`),
};

// ===== TOOL CONDITIONS API =====
export const toolConditionsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    return apiRequest<{ data: any[]; totalPages?: number; totalItems?: number }>(
      `/tool-conditions?${searchParams.toString()}`,
    );
  },
  create: (data: { name: string }) =>
    apiRequest<any>('/tool-conditions', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<{ name: string }>) =>
    apiRequest<any>(`/tool-conditions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest<void>(`/tool-conditions/${id}`, { method: 'DELETE' }),
  seed: () =>
    apiRequest<{ message: string; created: number; skipped: number }>('/tool-conditions/seed', {
      method: 'POST',
    }),
};

// ===== TOOLS API (OpenAPI pattern) =====
export const toolsOpenApi = {
  list: (params?: { categoryId?: string; available?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.available) queryParams.append('available', 'true');
    const query = queryParams.toString();
    return apiGet<ToolApi[]>(`/tools${query ? `?${query}` : ''}`);
  },
  create: (data: CreateToolRequest) => apiPost<ToolApi>('/tools', data),
  update: (id: string, data: UpdateToolRequest) => apiPatch<ToolApi>(`/tools/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/tools/${id}`),
};

// ===== LOCATIONS API (OpenAPI pattern) =====
export const locationsOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: LocationApi[]; totalPages?: number; totalItems?: number }>(
      `/locations${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<LocationApi>(`/locations/${id}`),
  create: (data: CreateLocationRequest) => apiPost<LocationApi>('/locations', data),
  update: (id: string, data: UpdateLocationRequest) =>
    apiPatch<LocationApi>(`/locations/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/locations/${id}`),
};

// ===== PART CATEGORIES API (OpenAPI pattern) =====
export const partCategoriesOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: PartCategoryApi[]; totalPages?: number; totalItems?: number }>(
      `/part-categories${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartCategoryApi>(`/part-categories/${id}`),
  create: (data: CreatePartCategoryRequest) => apiPost<PartCategoryApi>('/part-categories', data),
  update: (id: string, data: UpdatePartCategoryRequest) =>
    apiPatch<PartCategoryApi>(`/part-categories/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/part-categories/${id}`),
};

// ===== PART UNITS API (OpenAPI pattern) =====
export const partUnitsOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: PartUnitApi[]; totalPages?: number; totalItems?: number }>(
      `/part-units${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartUnitApi>(`/part-units/${id}`),
  create: (data: CreatePartUnitRequest) => apiPost<PartUnitApi>('/part-units', data),
  update: (id: string, data: UpdatePartUnitRequest) =>
    apiPatch<PartUnitApi>(`/part-units/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/part-units/${id}`),
};

// ===== TOOL CATEGORIES API (OpenAPI pattern) =====
export const toolCategoriesOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: ToolCategoryApi[]; totalPages?: number; totalItems?: number }>(
      `/tool-categories${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ToolCategoryApi>(`/tool-categories/${id}`),
  create: (data: CreateToolCategoryRequest) => apiPost<ToolCategoryApi>('/tool-categories', data),
  update: (id: string, data: UpdateToolCategoryRequest) =>
    apiPatch<ToolCategoryApi>(`/tool-categories/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/tool-categories/${id}`),
};

// ===== PART TYPES API (OpenAPI pattern) =====
export const partTypesApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    categoryId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    if (params?.categoryId) searchParams.append('categoryId', params.categoryId);
    const query = searchParams.toString();
    return apiGet<{ data: PartTypeApi[]; totalPages?: number; totalItems?: number }>(
      `/part-types${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartTypeApi>(`/part-types/${id}`),
  create: (data: CreatePartTypeRequest) => apiPost<PartTypeApi>('/part-types', data),
  update: (id: string, data: UpdatePartTypeRequest) =>
    apiPatch<PartTypeApi>(`/part-types/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/part-types/${id}`),
};

// ===== TOOL MODELS API (OpenAPI pattern) =====
export const toolModelsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return apiGet<{ data: ToolModelApi[]; totalPages?: number; totalItems?: number }>(
      `/tool-models${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ToolModelApi>(`/tool-models/${id}`),
  create: (data: CreateToolModelRequest) => apiPost<ToolModelApi>('/tool-models', data),
  update: (id: string, data: UpdateToolModelRequest) =>
    apiPatch<ToolModelApi>(`/tool-models/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/tool-models/${id}`),
};

// ===== TOOL CALIBRATIONS API (OpenAPI pattern) =====
export const toolCalibrationsApi = {
  list: (params?: { toolId?: string; page?: number; limit?: number; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.toolId) queryParams.append('toolId', params.toolId);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return apiGet<{ data: ToolCalibrationApi[]; totalPages?: number; totalItems?: number }>(
      `/tool-calibrations${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ToolCalibrationApi>(`/tool-calibrations/${id}`),
  create: (data: CreateToolCalibrationRequest) =>
    apiPost<ToolCalibrationApi>('/tool-calibrations', data),
  getToolHistory: (toolId: string) =>
    apiGet<{ data: ToolCalibrationApi[] }>(`/tool-calibrations/tools/${toolId}/calibrations`),
  getCalendarEvents: (params: { startDate: string; endDate: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', params.startDate);
    queryParams.append('endDate', params.endDate);
    if (params.status) queryParams.append('status', params.status);
    const query = queryParams.toString();
    return apiGet<{ data: CalibrationEvent[] }>(
      `/tool-calibrations/calendar${query ? `?${query}` : ''}`,
    );
  },
};

// ===== ASSETS API =====
export const assetsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: string;
    assetType?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    if (params?.assetType) searchParams.append('assetType', params.assetType);
    const query = searchParams.toString();
    return apiGet<Asset[] | { data: Asset[]; totalPages: number; totalItems: number }>(
      `/assets${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Asset>(`/assets/${id}`),
  create: (data: NewAsset) => apiPost<Asset>('/assets', data),
  update: (id: string, data: AssetUpdateOpenApi) => apiPatch<Asset>(`/assets/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/assets/${id}`),
  bulkCreate: (
    items: Array<{
      name: string;
      code: string;
      assetType: string;
      location: string;
      parentAssetId?: string | null;
      serialNumber?: string | null;
      manufacturer?: string | null;
      model?: string | null;
      purchaseDate?: string | null;
      warrantyExpiry?: string | null;
      status?: string | null;
    }>,
  ) =>
    apiPost<{
      success: boolean;
      created: number;
      failed: number;
      errors: Record<string, string[]>;
    }>('/assets/bulk', { items }),
  getHierarchy: (id: string) => apiGet<Asset & { children?: Asset[] }>(`/assets/${id}/hierarchy`),
  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<{ imageUrl: string }>(`/assets/${id}/image`, formData, {
      headers: {}, // Let browser set Content-Type with boundary for FormData
    });
  },
  getTree: (includeAll: boolean = true) => apiGet<any[]>(`/assets/tree?includeAll=${includeAll}`),
  getPartTypes: (assetId: string) =>
    apiGet<Array<{ id: string; name: string; quantityRequired?: number; notes?: string | null }>>(
      `/assets/${assetId}/part-types`,
    ),
};

// ===== ASSET PARAMETERS API =====
export const assetParametersApi = {
  getAll: (params?: { assetId?: string; page?: number; limit?: number; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.assetId) queryParams.append('assetId', params.assetId);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return apiGet<
      AssetParameter[] | { data: AssetParameter[]; totalPages: number; totalItems: number }
    >(`/asset-parameters${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<AssetParameter>(`/asset-parameters/${id}`),
  create: (data: NewAssetParameter) => apiPost<AssetParameter>('/asset-parameters', data),
  update: (id: string, data: Partial<NewAssetParameter>) =>
    apiPatch<AssetParameter>(`/asset-parameters/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/asset-parameters/${id}`),
};

// ===== ASSET PARAMETER VALUES API =====
export const assetParameterValuesApi = {
  getAll: (params?: { assetParameterId?: string; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.assetParameterId) queryParams.append('assetParameterId', params.assetParameterId);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return apiGet<AssetParameterValue[]>(`/asset-parameter-values/raw${query ? `?${query}` : ''}`);
  },
  getAllForChart: (params?: { assetParameterId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.assetParameterId) queryParams.append('assetParameterId', params.assetParameterId);
    const query = queryParams.toString();
    return apiGet<AssetParameterValue[]>(`/asset-parameter-values${query ? `?${query}` : ''}`);
  },
  create: (data: NewAssetParameterValue) =>
    apiPost<AssetParameterValue>('/asset-parameter-values', data),
  update: (id: string, data: Partial<NewAssetParameterValue>) =>
    apiPatch<AssetParameterValue>(`/asset-parameter-values/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/asset-parameter-values/${id}`),
};

// ===== ASSET PARTS API =====
export const assetPartsApi = {
  list: (assetId?: string) => {
    const params = new URLSearchParams();
    if (assetId) params.append('assetId', assetId);
    const query = params.toString();
    return apiGet<AssetPart[]>(`/asset-parts${query ? `?${query}` : ''}`);
  },
  create: (data: NewAssetPart) => apiPost<AssetPart>('/asset-parts', data),
  update: (id: string, data: Partial<NewAssetPart>) =>
    apiPatch<AssetPart>(`/asset-parts/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/asset-parts/${id}`),
};

// ===== ASSET PART TYPES API =====
export interface AssetPartTypeWithDetails {
  id: string;
  assetId: string;
  assetName: string | null;
  assetCode: string | null;
  partTypeId: string;
  partTypeName: string | null;
  quantityRequired: number;
  notes: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface NewAssetPartTypeRequest {
  assetId: string;
  partTypeId: string;
  quantityRequired?: number;
  notes?: string | null;
}

export interface UpdateAssetPartTypeRequest {
  quantityRequired?: number;
  notes?: string | null;
}

export const assetPartTypesApi = {
  getAll: (params?: { assetId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.assetId) queryParams.append('assetId', params.assetId);
    const query = queryParams.toString();
    return apiGet<AssetPartTypeWithDetails[]>(
      `/assets/asset-part-types${query ? `?${query}` : ''}`,
    );
  },
  create: (data: NewAssetPartTypeRequest) =>
    apiPost<AssetPartType>('/assets/asset-part-types', data),
  update: (id: string, data: UpdateAssetPartTypeRequest) =>
    apiPatch<AssetPartType>(`/assets/asset-part-types/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/assets/asset-part-types/${id}`),
};

// ===== MACHINES API (OpenAPI pattern) =====
// Now queries /assets with assetType=machine filter
export const machinesOpenApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    // Always filter for machine type
    searchParams.append('assetType', 'machine');
    const query = searchParams.toString();
    return apiGet<Machine[] | { data: Machine[]; totalPages: number; totalItems: number }>(
      `/assets${query ? `?${query}` : ''}`,
    );
  },
  create: (data: NewMachine) => apiPost<Machine>('/assets', data),
  update: (id: string, data: Partial<NewMachine>) => apiPatch<Machine>(`/assets/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/assets/${id}`),
};

// ===== ROLES API (OpenAPI pattern) =====
export const rolesOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: RoleApi[]; totalPages?: number; totalItems?: number }>(
      `/roles${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<RoleApi>(`/roles/${id}`),
  create: (data: CreateRoleRequest) => apiPost<RoleApi>('/roles', data),
  update: (id: string, data: UpdateRoleRequest) => apiPatch<RoleApi>(`/roles/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/roles/${id}`),
};

// ===== GROUPS API (OpenAPI pattern) =====
export const groupsOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: GroupApi[]; totalPages?: number; totalItems?: number }>(
      `/groups${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<GroupApi>(`/groups/${id}`),
  create: (data: CreateGroupRequest) => apiPost<GroupApi>('/groups', data),
  update: (id: string, data: UpdateGroupRequest) => apiPatch<GroupApi>(`/groups/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/groups/${id}`),
};

// ===== SHIFTS API (OpenAPI pattern) =====
export const shiftsOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: ShiftApi[]; totalPages?: number; totalItems?: number }>(
      `/shifts${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ShiftApi>(`/shifts/${id}`),
  create: (data: CreateShiftRequest) => apiPost<ShiftApi>('/shifts', data),
  update: (id: string, data: UpdateShiftRequest) => apiPatch<ShiftApi>(`/shifts/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/shifts/${id}`),
};

// ===== MAINTENANCE TICKETS API (OpenAPI pattern) =====
export const maintenanceTicketsOpenApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    search?: string;
    status?: string;
    priority?: string;
    type?: string;
    assetId?: string;
    assignedTo?: string;
    startDate?: string;
    endDate?: string;
    dateField?: 'createdAt' | 'scheduledAt';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.type) searchParams.append('type', params.type);
    if (params?.assetId) searchParams.append('assetId', params.assetId);
    if (params?.assignedTo) searchParams.append('assignedTo', params.assignedTo);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.dateField) searchParams.append('dateField', params.dateField);
    const query = searchParams.toString();
    return apiGet<{
      data: MaintenanceTicketApi[];
      pagination: { page: number; limit: number; totalItems: number; totalPages: number };
    }>(`/maintenance-tickets${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<MaintenanceTicketApi>(`/maintenance-tickets/${id}`),
  create: (data: CreateMaintenanceTicketRequest) =>
    apiPost<MaintenanceTicketApi>('/maintenance-tickets', data),
  update: (id: string, data: UpdateMaintenanceTicketRequest) =>
    apiPatch<MaintenanceTicketApi>(`/maintenance-tickets/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/maintenance-tickets/${id}`),
};

// ===== INVENTORY CATEGORIES API =====
export const partCategoriesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return apiGet<{ data: PartCategory[]; totalPages?: number; totalItems?: number }>(
      `/part-categories${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartCategory>(`/part-categories/${id}`),
  create: (data: NewPartCategory) => apiPost<PartCategory>('/part-categories', data),
  update: (id: string, data: Partial<NewPartCategory>) =>
    apiPatch<PartCategory>(`/part-categories/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/part-categories/${id}`),
};

// ===== PART CATEGORY ATTRIBUTES API =====
export const partCategoryAttributesApi = {
  getAll: (params?: { categoryId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.categoryId) searchParams.append('categoryId', params.categoryId);
    const query = searchParams.toString();
    return apiGet<{ data: PartCategoryAttribute[] }>(
      `/part-category-attributes${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartCategoryAttribute>(`/part-category-attributes/${id}`),
  create: (data: NewPartCategoryAttribute) =>
    apiPost<PartCategoryAttribute>('/part-category-attributes', data),
  update: (id: string, data: Partial<NewPartCategoryAttribute>) =>
    apiPatch<PartCategoryAttribute>(`/part-category-attributes/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/part-category-attributes/${id}`),
  generateCanonical: (categoryId: string, attributes: Array<{ key: string; value: string }>) =>
    apiPost<CanonicalPreviewResponse>('/part-category-attributes/canonical', {
      categoryId,
      attributes,
    }),
  seed: () =>
    apiPost<{
      message: string;
      count: number;
      alreadyExisted: number;
      totalCategories: number;
      debug: { availableCategories: Array<{ id: string; name: string }> };
    }>('/part-category-attributes/seed', {}),
};

// ===== INVENTORY UNITS API =====
export const partUnitsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const query = searchParams.toString();
    return apiGet<{ data: PartUnit[]; totalPages?: number; totalItems?: number }>(
      `/part-units${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartUnit>(`/part-units/${id}`),
  create: (data: NewPartUnit) => apiPost<PartUnit>('/part-units', data),
  update: (id: string, data: Partial<NewPartUnit>) => apiPatch<PartUnit>(`/part-units/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/part-units/${id}`),
};

// ===== PART ITEM BATCHES API =====
export const partItemBatchesApi = {
  getAll: () => apiGet<PartItemBatch[]>('/part-item-batches'),
  getByPartItemId: (
    partItemId: string,
    params?: { page?: number; limit?: number; search?: string },
  ) => {
    if (!params) {
      // No pagination params - return array (backward compatible)
      return apiGet<(PartItemBatch & { availableQuantity?: number })[]>(
        `/part-item-batches/part-item/${partItemId}`,
      );
    }

    // With pagination params - build query string
    const searchParams = new URLSearchParams();
    if (params.page !== undefined) searchParams.set('page', String(params.page));
    if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params.search) searchParams.set('search', params.search);
    const queryString = searchParams.toString();
    const url = `/part-item-batches/part-item/${partItemId}${queryString ? `?${queryString}` : ''}`;

    // Return union type to handle both responses
    return apiGet<
      | (PartItemBatch & { availableQuantity?: number })[]
      | {
          data: (PartItemBatch & { availableQuantity?: number })[];
          pagination: {
            page: number;
            limit: number;
            total: number;
            totalPages: number;
            hasNext: boolean;
          };
        }
    >(url);
  },
  getById: (id: string) => apiGet<PartItemBatch>(`/part-item-batches/${id}`),
  create: (data: NewPartItemBatch) => apiPost<PartItemBatch>('/part-item-batches', data),
  update: (id: string, data: Partial<NewPartItemBatch>) =>
    apiPatch<PartItemBatch>(`/part-item-batches/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/part-item-batches/${id}`),
};

// ===== PART ITEM CHECKOUTS API =====
export const partItemCheckoutsApi = {
  getAll: (params?: { technicianId?: string; status?: string; batchId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.technicianId) searchParams.append('technicianId', params.technicianId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.batchId) searchParams.append('batchId', params.batchId);
    const query = searchParams.toString();
    return apiGet<{ data: PartItemCheckout[]; totalPages: number; totalItems: number }>(
      `/part-item-checkouts${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartItemCheckout>(`/part-item-checkouts/${id}`),
  getByTechnician: (technicianId: string) =>
    apiGet<PartItemCheckout[]>(`/part-item-checkouts/technician/${technicianId}`),
  checkout: (data: NewPartItemCheckout) => apiPost<PartItemCheckout>('/part-item-checkouts', data),
  return: (id: string, data: { quantity?: number; notes?: string }) =>
    apiPatch<PartItemCheckout>(`/part-item-checkouts/${id}/return`, data),
  reportLoss: (id: string, data: { quantity: number; reason: string; notes?: string }) =>
    apiPatch<PartItemCheckout>(`/part-item-checkouts/${id}/loss`, data),
  delete: (id: string) => apiDelete<void>(`/part-item-checkouts/${id}`),
};

// ===== PART ITEM BATCH TRANSACTIONS API =====
export const partItemBatchTransactionsApi = {
  getAll: () => apiGet<PartItemBatchTransaction[]>('/part-item-batch-transactions'),
  getByBatchId: (batchId: string) =>
    apiGet<PartItemBatchTransaction[]>(`/part-item-batch-transactions/batch/${batchId}`),
  getById: (id: string) => apiGet<PartItemBatchTransaction>(`/part-item-batch-transactions/${id}`),
  create: (data: NewPartItemBatchTransaction) =>
    apiPost<PartItemBatchTransaction>('/part-item-batch-transactions', data),
  update: (id: string, data: Partial<NewPartItemBatchTransaction>) =>
    apiPatch<PartItemBatchTransaction>(`/part-item-batch-transactions/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/part-item-batch-transactions/${id}`),
};

// ===== SUPPLIERS API =====
export const suppliersApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: Supplier[]; totalPages?: number; totalItems?: number }>(
      `/suppliers${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Supplier>(`/suppliers/${id}`),
  create: (data: NewSupplier) => apiPost<Supplier>('/suppliers', data),
  update: (id: string, data: Partial<NewSupplier>) => apiPatch<Supplier>(`/suppliers/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/suppliers/${id}`),
};

// ===== SUPPLIERS API (OpenAPI pattern) =====
export const suppliersOpenApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: SupplierApi[]; totalPages?: number; totalItems?: number }>(
      `/suppliers${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<SupplierApi>(`/suppliers/${id}`),
  create: (data: CreateSupplierRequest) => apiPost<SupplierApi>('/suppliers', data),
  update: (id: string, data: UpdateSupplierRequest) =>
    apiPatch<SupplierApi>(`/suppliers/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/suppliers/${id}`),
};

// ===== INSTALLED PARTS API =====
export const installedPartsApi = {
  getAll: (params?: { assetId?: string; ticketId?: string; batchId?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.assetId) queryParams.append('assetId', params.assetId);
    if (params?.ticketId) queryParams.append('ticketId', params.ticketId);
    if (params?.batchId) queryParams.append('batchId', params.batchId);
    const query = queryParams.toString();
    return apiGet<InstalledPart[]>(`/installed-parts${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<InstalledPart>(`/installed-parts/${id}`),
  create: (data: NewInstalledPart) => apiPost<InstalledPart>('/installed-parts', data),
  update: (id: string, data: Partial<NewInstalledPart>) =>
    apiPatch<InstalledPart>(`/installed-parts/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/installed-parts/${id}`),
};

// ===== TOOL TRANSACTIONS API =====
export const toolTransactionsApi = {
  getAll: (params?: {
    ticketId?: string;
    toolId?: string;
    technicianId?: string;
    status?: 'checked_out' | 'returned';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.ticketId) queryParams.append('ticketId', params.ticketId);
    if (params?.toolId) queryParams.append('toolId', params.toolId);
    if (params?.technicianId) queryParams.append('technicianId', params.technicianId);
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString();
    return apiGet<ToolTransaction[]>(`/tool-transactions${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<ToolTransaction>(`/tool-transactions/${id}`),
  create: (data: NewToolTransaction) => apiPost<ToolTransaction>('/tool-transactions', data),
  return: (id: string) => apiPatch<ToolTransaction>(`/tool-transactions/${id}/return`, {}),
  update: (id: string, data: Partial<NewToolTransaction>) =>
    apiPatch<ToolTransaction>(`/tool-transactions/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/tool-transactions/${id}`),
};

// ===== PROBLEMS API =====
export const problemsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; groupId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.groupId) searchParams.append('groupId', params.groupId);
    const query = searchParams.toString();
    return apiGet<Problem[] | { data: Problem[]; totalPages?: number; totalItems?: number }>(
      `/problems${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Problem>(`/problems/${id}`),
  create: (data: NewProblem) => apiPost<Problem>('/problems', data),
  update: (id: string, data: Partial<NewProblem>) => apiPatch<Problem>(`/problems/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/problems/${id}`),
};

// ===== PROBLEM GROUPS API =====
export const problemGroupsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<{ data: ProblemGroup[]; totalPages: number; totalItems: number }>(
      `/problem-groups${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ProblemGroup>(`/problem-groups/${id}`),
  create: (data: NewProblemGroup) => apiPost<ProblemGroup>('/problem-groups', data),
  update: (id: string, data: Partial<NewProblemGroup>) =>
    apiPatch<ProblemGroup>(`/problem-groups/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/problem-groups/${id}`),
};

// ===== PROBLEM ACTIONS API =====
export const problemActionsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; problemId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.problemId) searchParams.append('problemId', params.problemId);
    const query = searchParams.toString();
    return apiGet<ProblemAction[]>(`/problem-actions${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<ProblemAction>(`/problem-actions/${id}`),
  create: (data: NewProblemAction) => apiPost<ProblemAction>('/problem-actions', data),
  update: (id: string, data: Partial<NewProblemAction>) =>
    apiPatch<ProblemAction>(`/problem-actions/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/problem-actions/${id}`),
};

// ===== PROBLEM SUB ACTIONS API =====
export const problemSubActionsApi = {
  getAll: (params?: { problemActionId?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.problemActionId) searchParams.append('problemActionId', params.problemActionId);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiGet<{ data: ProblemSubAction[]; totalPages: number; totalItems: number }>(
      `/problem-sub-actions${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<ProblemSubAction>(`/problem-sub-actions/${id}`),
  create: (data: NewProblemSubAction) => apiPost<ProblemSubAction>('/problem-sub-actions', data),
  update: (id: string, data: Partial<NewProblemSubAction>) =>
    apiPatch<ProblemSubAction>(`/problem-sub-actions/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/problem-sub-actions/${id}`),
};

// ===== PROBLEM ASSETS API =====
export const problemAssetsApi = {
  getAll: (params?: { problemId?: string; assetId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.problemId) searchParams.append('problemId', params.problemId);
    if (params?.assetId) searchParams.append('assetId', params.assetId);
    const query = searchParams.toString();
    return apiGet<ProblemAsset[]>(`/problem-assets${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<ProblemAsset>(`/problem-assets/${id}`),
  create: (data: NewProblemAsset) => apiPost<ProblemAsset>('/problem-assets', data),
  update: (id: string, data: Partial<NewProblemAsset>) =>
    apiPatch<ProblemAsset>(`/problem-assets/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/problem-assets/${id}`),
  // Sub-actions for a specific problem-asset link
  getSubActions: (problemAssetId: string) =>
    apiGet<ProblemAssetSubAction[]>(`/problem-assets/${problemAssetId}/sub-actions`),
  createSubAction: (problemAssetId: string, data: NewProblemAssetSubAction) =>
    apiPost<ProblemAssetSubAction>(`/problem-assets/${problemAssetId}/sub-actions`, data),
  updateSubAction: (
    problemAssetId: string,
    subActionId: string,
    data: Partial<NewProblemAssetSubAction>,
  ) =>
    apiPatch<ProblemAssetSubAction>(
      `/problem-assets/${problemAssetId}/sub-actions/${subActionId}`,
      data,
    ),
  deleteSubAction: (problemAssetId: string, subActionId: string) =>
    apiDelete<{ message: string }>(`/problem-assets/${problemAssetId}/sub-actions/${subActionId}`),
};

// ===== SYMPTOMS API =====
export const symptomsApi = {
  getAll: (params?: {
    problemId?: string;
    symptomId?: string;
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.problemId) queryParams.append('problemId', params.problemId);
    if (params?.symptomId) queryParams.append('symptomId', params.symptomId);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.order) queryParams.append('order', params.order);

    const query = queryParams.toString();
    return apiGet<{ data: Symptom[]; totalPages: number; totalItems: number }>(
      `/symptoms${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<Symptom>(`/symptoms/${id}`),
  create: (data: NewSymptom) => apiPost<Symptom>('/symptoms', data),
  update: (id: string, data: Partial<NewSymptom>) => apiPatch<Symptom>(`/symptoms/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/symptoms/${id}`),
};

// ===== SYMPTOM PROBLEM LINKS API =====
export const symptomProblemLinksApi = {
  getAll: (params?: { symptomId?: string; problemId?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.symptomId) searchParams.append('symptomId', params.symptomId);
    if (params?.problemId) searchParams.append('problemId', params.problemId);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiGet<{
      data: (SymptomProblemLink & { symptomName?: string; problemName?: string })[];
      totalPages: number;
      totalItems: number;
    }>(`/symptom-problem-links${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<SymptomProblemLink>(`/symptom-problem-links/${id}`),
  create: (data: NewSymptomProblemLink) =>
    apiPost<SymptomProblemLink>('/symptom-problem-links', data),
  update: (id: string, data: Partial<NewSymptomProblemLink>) =>
    apiPatch<SymptomProblemLink>(`/symptom-problem-links/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/symptom-problem-links/${id}`),
};

// ===== SYMPTOM PARAMETER LINKS API =====
export const symptomParameterLinksApi = {
  getAll: () => apiGet<SymptomParameterLink[]>('/symptom-parameter-links'),
  getById: (id: string) => apiGet<SymptomParameterLink>(`/symptom-parameter-links/${id}`),
  create: (data: NewSymptomParameterLink) =>
    apiPost<SymptomParameterLink>('/symptom-parameter-links', data),
  update: (id: string, data: Partial<NewSymptomParameterLink>) =>
    apiPatch<SymptomParameterLink>(`/symptom-parameter-links/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/symptom-parameter-links/${id}`),
};

// Export types for convenience
export type {
  AssetParameter,
  AssetParameterValue,
  AuthResponse,
  CreateGroupRequest,
  CreateLocationRequest,
  CreateMaintenanceTicketRequest,
  CreatePartCategoryRequest,
  CreatePartItemRequest,
  // Generated request types for OpenAPI-pattern APIs
  CreatePartTypeRequest,
  CreatePartUnitRequest,
  CreateRoleRequest,
  CreateShiftRequest,
  CreateSupplierRequest,
  CreateToolCalibrationRequest,
  CreateToolCategoryRequest,
  CreateToolModelRequest,
  CreateToolRequest,
  Group,
  LoginCredentials,
  Machine,
  MaintenanceTicket,
  NewAsset,
  NewAssetParameter,
  NewAssetParameterValue,
  NewGroup,
  NewMachine,
  NewMaintenanceTicket,
  NewParameter,
  // PartCategory, // imported via alias below
  NewPartCategory,
  NewPartItem,
  // PartUnit, // imported via alias below
  NewPartUnit,
  NewProblem,
  NewProblemAction,
  NewProblemGroup,
  NewProblemSubAction,
  NewRole,
  NewShift,
  NewShiftPattern,
  NewShiftPatternDetail,
  NewShiftRoster,
  NewShiftSlot,
  NewSymptom,
  NewSymptomParameterLink,
  NewSymptomProblemLink,
  NewTicketRule,
  NewTicketRuleExecution,
  NewTicketSymptom,
  NewUser,
  NewUserGroup,
  NewUserRole,
  Parameter,
  PartItem,
  PartTypeApi,
  Problem,
  ProblemAction,
  ProblemGroup,
  ProblemSubAction,
  RegisterCredentials,
  Role,
  Shift,
  ShiftPattern,
  ShiftPatternDetail,
  ShiftRoster,
  ShiftSlot,
  Symptom,
  SymptomParameterLink,
  SymptomProblemLink,
  TicketRule,
  TicketRuleExecution,
  TicketSymptom,
  ToolCalibrationApi,
  ToolModelApi,
  UpdateGroupRequest,
  UpdateLocationRequest,
  UpdateMaintenanceTicketRequest,
  UpdatePartCategoryRequest,
  UpdatePartItemRequest,
  UpdatePartTypeRequest,
  UpdatePartUnitRequest,
  UpdateRoleRequest,
  UpdateShiftRequest,
  UpdateSupplierRequest,
  UpdateToolCalibrationRequest,
  UpdateToolCategoryRequest,
  UpdateToolModelRequest,
  UpdateToolRequest,
  User,
  UserGroup,
  UserRole,
};

// Type aliases for convenience
export type PartType = PartTypeApi;
export type ToolModel = ToolModelApi;
export type ToolCalibration = ToolCalibrationApi;
export type Tool = ToolApi;
export type ToolCategory = ToolCategoryApi;
export type Supplier = SupplierApi;
export type PartCategory = PartCategoryApi;
export type PartUnit = PartUnitApi;
export type Location = LocationApi;
export type Asset = AssetOpenApi;

// Tool calibration calendar event type
export type CalibrationEvent = {
  id: string;
  title: string;
  start: string;
  end: string;
  color?: string;
  resource: {
    toolId: string;
    toolName: string;
    toolCode: string;
    status: 'overdue' | 'due_soon' | 'ok' | 'never_calibrated';
  };
};

// Part Category Attribute types
export type AttributeType = 'number' | 'select' | 'text';

export interface PartCategoryAttribute {
  id: string;
  categoryId: string;
  key: string;
  label: string;
  type: AttributeType;
  required: boolean;
  options: string | null;
  priority: number;
  createdAt: string;
}

export interface NewPartCategoryAttribute {
  categoryId: string;
  key: string;
  label: string;
  type: AttributeType;
  required?: boolean;
  options?: string;
  priority?: number;
}

export interface CategoryAttributeResponse {
  data: PartCategoryAttribute;
}

export interface CanonicalPreviewResponse {
  canonicalName: string;
  categoryId: string;
  attributes: Array<{ key: string; value: string }>;
}

// Type aliases for create operations (partial types since server generates id, timestamps, etc.)
export type NewPartType = Partial<PartTypeApi>;
export type NewToolModel = Partial<ToolModelApi>;
export type NewToolCalibration = Partial<ToolCalibrationApi>;

// ===== TICKET RULES API =====
export const ticketRulesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.order) queryParams.append('order', params.order);

    const queryString = queryParams.toString();
    const url = queryString ? `/ticket-rules?${queryString}` : '/ticket-rules';

    return apiGet<TicketRule[] | { data: TicketRule[]; totalPages: number; totalItems: number }>(
      url,
    );
  },
  getById: (id: string) => apiGet<TicketRule>(`/ticket-rules/${id}`),
  create: (data: NewTicketRule) => apiPost<TicketRule>('/ticket-rules', data),
  update: (id: string, data: Partial<NewTicketRule>) =>
    apiPatch<TicketRule>(`/ticket-rules/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/ticket-rules/${id}`),
};

// ===== TICKET RULE EXECUTIONS API =====
export const ticketRuleExecutionsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'triggered' | 'expired';
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.order) queryParams.append('order', params.order);

    const queryString = queryParams.toString();
    const url = queryString ? `/ticket-rule-executions?${queryString}` : '/ticket-rule-executions';

    return apiGet<
      | TicketRuleExecution[]
      | { data: TicketRuleExecution[]; totalPages: number; totalItems: number }
    >(url);
  },
  retry: (id: string) =>
    apiPost<{ message: string; execution: TicketRuleExecution }>(
      `/ticket-rule-executions/${id}/retry`,
      {},
    ),
};

// ===== TICKET SYMPTOMS API =====
export const ticketSymptomsApi = {
  getByTicketId: (ticketId: string) => {
    const queryParams = new URLSearchParams({ ticketId });
    return apiGet<TicketSymptom[]>(`/ticket-symptoms?${queryParams.toString()}`);
  },
  create: (data: NewTicketSymptom) => apiPost<TicketSymptom>('/ticket-symptoms', data),
  delete: (id: string) => apiDelete<void>(`/ticket-symptoms/${id}`),
};

// ===== TICKET PRIORITIES API =====
export const ticketPrioritiesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.active !== undefined) searchParams.append('active', params.active.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    const query = searchParams.toString();
    return apiGet<{
      data: TicketPriority[];
      totalPages?: number;
      totalItems?: number;
      page?: number;
      limit?: number;
    }>(`/ticket-priorities${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<TicketPriority>(`/ticket-priorities/${id}`),
  create: (data: NewTicketPriority) => apiPost<TicketPriority>('/ticket-priorities', data),
  update: (id: string, data: Partial<NewTicketPriority>) =>
    apiPatch<TicketPriority>(`/ticket-priorities/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/ticket-priorities/${id}`),
  seed: () => apiPost<{ message: string; count: number }>('/ticket-priorities/seed', {}),
};

// ===== TICKET STATUSES API =====
export const ticketStatusesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.active !== undefined) searchParams.append('active', params.active.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    const query = searchParams.toString();
    return apiGet<{
      data: TicketStatusRecord[];
      totalPages?: number;
      totalItems?: number;
      page?: number;
      limit?: number;
    }>(`/ticket-statuses${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<TicketStatusRecord>(`/ticket-statuses/${id}`),
  create: (data: NewTicketStatusRecord) => apiPost<TicketStatusRecord>('/ticket-statuses', data),
  update: (id: string, data: Partial<NewTicketStatusRecord>) =>
    apiPatch<TicketStatusRecord>(`/ticket-statuses/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/ticket-statuses/${id}`),
  seed: () => apiPost<{ message: string; count: number }>('/ticket-statuses/seed', {}),
};

// ===== EFFORT LEVELS API =====
export const effortLevelsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    active?: boolean;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.active !== undefined) searchParams.append('active', params.active.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    const query = searchParams.toString();
    return apiGet<{
      data: EffortLevel[];
      totalPages?: number;
      totalItems?: number;
      page?: number;
      limit?: number;
    }>(`/effort-levels${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<EffortLevel>(`/effort-levels/${id}`),
  create: (data: NewEffortLevel) => apiPost<EffortLevel>('/effort-levels', data),
  update: (id: string, data: Partial<NewEffortLevel>) =>
    apiPatch<EffortLevel>(`/effort-levels/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/effort-levels/${id}`),
  seed: () => apiPost<{ message: string; count: number }>('/effort-levels/seed', {}),
  getActive: () => apiGet<{ data: EffortLevel[] }>('/effort-levels?active=true'),
};

// ===== CHECKLIST TEMPLATES API =====
export const checklistTemplatesApi = {
  getAll: (params?: {
    status?: string;
    category?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    const response = apiGet<
      ChecklistTemplate[] | { data: ChecklistTemplate[]; totalPages?: number; totalItems?: number }
    >(`/checklist-templates${query ? `?${query}` : ''}`);
    return response;
  },
  getById: (id: string) => apiGet<ChecklistTemplate>(`/checklist-templates/${id}`),
  create: (data: NewChecklistTemplate) => apiPost<ChecklistTemplate>('/checklist-templates', data),
  update: (id: string, data: Partial<NewChecklistTemplate>) =>
    apiPatch<ChecklistTemplate>(`/checklist-templates/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-templates/${id}`),
  publish: (id: string) => apiPost<ChecklistTemplate>(`/checklist-templates/${id}/publish`, {}),
  getVersions: (id: string) => apiGet<any[]>(`/checklist-templates/${id}/versions`),
  rollback: (id: string, version: number) =>
    apiPost<ChecklistTemplate>(`/checklist-templates/${id}/rollback/${version}`, {}),
};

// ===== CHECKLIST TEMPLATE SECTIONS API =====
export const checklistTemplateSectionsApi = {
  getAll: (templateId: string) => {
    const queryParams = new URLSearchParams({ templateId });
    return apiGet<ChecklistTemplateSection[]>(
      `/checklist-template-sections?${queryParams.toString()}`,
    );
  },
  create: (data: NewChecklistTemplateSection) =>
    apiPost<ChecklistTemplateSection>('/checklist-template-sections', data),
  update: (id: string, data: Partial<NewChecklistTemplateSection>) =>
    apiPatch<ChecklistTemplateSection>(`/checklist-template-sections/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-template-sections/${id}`),
  reorder: (sectionOrders: Array<{ id: string; order: number }>) =>
    apiPost<void>('/checklist-template-sections/reorder', { sectionOrders }),
};

// ===== CHECKLIST TEMPLATE STEPS API =====
export const checklistTemplateStepsApi = {
  getAll: (sectionId: string) => {
    const queryParams = new URLSearchParams({ sectionId });
    return apiGet<ChecklistTemplateStep[]>(`/checklist-template-steps?${queryParams.toString()}`);
  },
  create: (data: NewChecklistTemplateStep) =>
    apiPost<ChecklistTemplateStep>('/checklist-template-steps', data),
  update: (id: string, data: Partial<NewChecklistTemplateStep>) =>
    apiPatch<ChecklistTemplateStep>(`/checklist-template-steps/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-template-steps/${id}`),
  reorder: (stepOrders: Array<{ id: string; order: number }>) =>
    apiPost<void>('/checklist-template-steps/reorder', { stepOrders }),
  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiRequest<ChecklistTemplateStep>(`/checklist-template-steps/${id}/upload-image`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type with boundary
    });
  },
};

// ===== CHECKLIST TEMPLATE FIELDS API =====
export const checklistTemplateFieldsApi = {
  getAll: (stepId: string) => {
    const queryParams = new URLSearchParams({ stepId });
    return apiGet<ChecklistTemplateField[]>(`/checklist-template-fields?${queryParams.toString()}`);
  },
  create: (data: NewChecklistTemplateField) =>
    apiPost<ChecklistTemplateField>('/checklist-template-fields', data),
  update: (id: string, data: Partial<NewChecklistTemplateField>) =>
    apiPatch<ChecklistTemplateField>(`/checklist-template-fields/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-template-fields/${id}`),
  reorder: (fieldOrders: Array<{ id: string; order: number }>) =>
    apiPost<void>('/checklist-template-fields/reorder', { fieldOrders }),
  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiRequest<ChecklistTemplateField>(`/checklist-template-fields/${id}/upload-image`, {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
};

// ===== CHECKLIST TEMPLATE FIELD OPTIONS API =====
export const checklistTemplateFieldOptionsApi = {
  getAll: (fieldId: string) => {
    const queryParams = new URLSearchParams({ fieldId });
    return apiGet<ChecklistTemplateFieldOption[]>(
      `/checklist-template-field-options?${queryParams.toString()}`,
    );
  },
  create: (data: NewChecklistTemplateFieldOption) =>
    apiPost<ChecklistTemplateFieldOption>('/checklist-template-field-options', data),
  update: (id: string, data: Partial<NewChecklistTemplateFieldOption>) =>
    apiPatch<ChecklistTemplateFieldOption>(`/checklist-template-field-options/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-template-field-options/${id}`),
};

// ===== CHECKLIST INSTANCES API =====
export const checklistInstancesApi = {
  getAll: (params?: {
    status?: string;
    templateId?: string;
    assignedTo?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.templateId) searchParams.append('templateId', params.templateId);
    if (params?.assignedTo) searchParams.append('assignedTo', params.assignedTo);
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return apiGet<
      ChecklistInstance[] | { data: ChecklistInstance[]; totalPages?: number; totalItems?: number }
    >(`/checklist-instances${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<ChecklistInstance>(`/checklist-instances/${id}`),
  create: (data: NewChecklistInstance) => apiPost<ChecklistInstance>('/checklist-instances', data),
  update: (id: string, data: Partial<NewChecklistInstance>) =>
    apiPatch<ChecklistInstance>(`/checklist-instances/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-instances/${id}`),
  complete: (id: string) => apiPost<ChecklistInstance>(`/checklist-instances/${id}/complete`, {}),
};

// ===== CHECKLIST FIELD RESPONSES API =====
export const checklistFieldResponsesApi = {
  getAll: (instanceId: string) => {
    const queryParams = new URLSearchParams({ instanceId });
    return apiGet<ChecklistFieldResponse[]>(`/checklist-field-responses?${queryParams.toString()}`);
  },
  create: (data: NewChecklistFieldResponse) =>
    apiPost<ChecklistFieldResponse>('/checklist-field-responses', data),
  update: (id: string, data: Partial<NewChecklistFieldResponse>) =>
    apiPatch<ChecklistFieldResponse>(`/checklist-field-responses/${id}`, data),
  delete: (id: string) => apiDelete<void>(`/checklist-field-responses/${id}`),
};

// ===== CHECKLIST ATTACHMENTS API =====
export const checklistAttachmentsApi = {
  getAll: (params: { instanceId: string; fieldId?: string; stepId?: string }) => {
    const searchParams = new URLSearchParams({ instanceId: params.instanceId });
    if (params.fieldId) searchParams.append('fieldId', params.fieldId);
    if (params.stepId) searchParams.append('stepId', params.stepId);
    return apiGet<ChecklistAttachment[]>(`/checklist-attachments?${searchParams.toString()}`);
  },
  upload: (
    file: File,
    instanceId: string,
    attachmentType: 'image' | 'file' | 'signature',
    instanceFieldId?: string,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('instanceId', instanceId);
    formData.append('attachmentType', attachmentType);
    if (instanceFieldId) formData.append('instanceFieldId', instanceFieldId);
    return apiRequest<ChecklistAttachment>('/checklist-attachments', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
  delete: (id: string) => apiDelete<void>(`/checklist-attachments/${id}`),
};

// ===== PREVENTIVE MAINTENANCE SCHEDULES API =====
export const preventiveMaintenanceSchedulesApi = {
  getAll: (params?: {
    assetId?: string;
    enabled?: boolean;
    scheduleType?: string;
    effortLevel?: string;
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.assetId) searchParams.append('assetId', params.assetId);
    if (params?.enabled !== undefined) searchParams.append('enabled', String(params.enabled));
    if (params?.scheduleType) searchParams.append('scheduleType', params.scheduleType);
    if (params?.effortLevel) searchParams.append('effortLevel', params.effortLevel);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.order) searchParams.append('order', params.order);
    const query = searchParams.toString();
    return apiGet<
      | PreventiveMaintenanceSchedule[]
      | { data: PreventiveMaintenanceSchedule[]; totalPages: number; totalItems: number }
    >(`/preventive-maintenance-schedules${query ? `?${query}` : ''}`);
  },
  getById: (id: string) =>
    apiGet<PreventiveMaintenanceSchedule>(`/preventive-maintenance-schedules/${id}`),
  create: (data: NewPreventiveMaintenanceSchedule) =>
    apiPost<PreventiveMaintenanceSchedule>('/preventive-maintenance-schedules', data),
  update: (id: string, data: Partial<NewPreventiveMaintenanceSchedule>) =>
    apiPatch<PreventiveMaintenanceSchedule>(`/preventive-maintenance-schedules/${id}`, data),
  delete: (id: string, cascade?: boolean) => {
    const url = `/preventive-maintenance-schedules/${id}${cascade ? '?cascade=true' : ''}`;
    return apiDelete<void>(url);
  },
  generateOccurrences: (id: string) =>
    apiPost<void>(`/preventive-maintenance-schedules/${id}/generate-occurrences`, {}),
  enable: (id: string) =>
    apiPost<PreventiveMaintenanceSchedule>(`/preventive-maintenance-schedules/${id}/enable`, {}),
  disable: (id: string) =>
    apiPost<PreventiveMaintenanceSchedule>(`/preventive-maintenance-schedules/${id}/disable`, {}),
};

// ===== MAINTENANCE OCCURRENCES API =====
export const maintenanceOccurrencesApi = {
  getAll: (params?: {
    scheduleId?: string;
    status?: string;
    assetId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.scheduleId) searchParams.append('scheduleId', params.scheduleId);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.assetId) searchParams.append('assetId', params.assetId);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    const query = searchParams.toString();
    return apiGet<MaintenanceOccurrence[]>(`/maintenance-occurrences${query ? `?${query}` : ''}`);
  },
  getById: (id: string) => apiGet<MaintenanceOccurrence>(`/maintenance-occurrences/${id}`),
  update: (id: string, data: Partial<NewMaintenanceOccurrence>) =>
    apiPatch<MaintenanceOccurrence>(`/maintenance-occurrences/${id}`, data),
  complete: (id: string, instanceId?: string) =>
    apiPost<MaintenanceOccurrence>(`/maintenance-occurrences/${id}/complete`, { instanceId }),
  skip: (id: string) => apiPost<MaintenanceOccurrence>(`/maintenance-occurrences/${id}/skip`, {}),
  delete: (id: string) => apiDelete<void>(`/maintenance-occurrences/${id}`),
  getByTicketId: (ticketId: string) =>
    apiGet<MaintenanceOccurrence | null>(`/maintenance-occurrences/by-ticket/${ticketId}`),
  reschedule: (id: string, newDate: string) =>
    apiPost<MaintenanceOccurrence>(`/maintenance-occurrences/${id}/reschedule`, { newDate }),
  getCalendarEvents: (params: {
    startDate: string;
    endDate: string;
    assetId?: string;
    status?: string;
    scheduleId?: string;
  }) => {
    const searchParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });
    if (params.assetId) searchParams.append('assetId', params.assetId);
    if (params.status) searchParams.append('status', params.status);
    if (params.scheduleId) searchParams.append('scheduleId', params.scheduleId);
    return apiGet<CalendarEvent[]>(`/maintenance-occurrences/calendar?${searchParams.toString()}`);
  },
  createChecklistInstance: (id: string) =>
    apiPost<MaintenanceOccurrence>(`/maintenance-occurrences/${id}/create-checklist-instance`, {}),
  linkTicket: (occurrenceId: string, ticketId: string) =>
    apiPost<{ message: string; link: any }>(
      `/maintenance-occurrences/${occurrenceId}/link-ticket/${ticketId}`,
      {},
    ),
};

// ===== GAMIFICATION & TRAINING API =====
type RuleWithConditions = RuleDefinition & { conditions?: RuleCondition[] };

export const gamificationApi = {
  getProfile: (userId: string) => apiGet<GamificationProfile>(`/gamification/profile/${userId}`),
  getLeaderboard: () =>
    apiGet<{
      data: Array<{
        id: string;
        name: string;
        email: string | null;
        profilePictureUrl?: string | null;
        points: number;
        level: number;
        badges: number;
        rank: number;
        init: string;
        streak: number;
        change: number;
      }>;
    }>('/gamification/leaderboard').then((r: any) => (Array.isArray(r) ? r : (r.data ?? []))),
  listTrainings: (params?: { search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return apiGet<Training[]>(`/gamification/trainings${query ? `?${query}` : ''}`).then(
      (r: any) => (Array.isArray(r) ? r : (r.data ?? [])),
    );
  },
  createTraining: (
    data: Pick<Training, 'name' | 'description' | 'checklistTemplateId' | 'isExternal'>,
  ) => apiPost<Training>('/gamification/trainings', data),
  updateTraining: (id: string, data: Partial<Training>) =>
    apiPatch<Training>(`/gamification/trainings/${id}`, data),
  deleteTraining: (id: string) =>
    apiRequest<{ success: boolean }>(`/gamification/trainings/${id}`, { method: 'DELETE' }),
  assignTraining: (trainingId: string, payload: { userId: string; dueDate?: string }) =>
    apiPost<UserTraining>(`/gamification/trainings/${trainingId}/assign`, payload),
  completeTraining: (userTrainingId: string, expiresAt?: string) =>
    apiPost<UserTraining>(`/gamification/user-trainings/${userTrainingId}/complete`, { expiresAt }),
  failTraining: (userTrainingId: string) =>
    apiPost<UserTraining>(`/gamification/user-trainings/${userTrainingId}/fail`, {}),
  deleteUserTraining: (userTrainingId: string) =>
    apiRequest<{ message: string }>(`/gamification/user-trainings/${userTrainingId}`, {
      method: 'DELETE',
    }),
  getUserTrainings: (userId?: string) => {
    const query = userId ? `?userId=${userId}` : '';
    return apiGet<UserTraining[]>(`/gamification/user-trainings${query}`).then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    );
  },
  createUserTrainingChecklistInstance: (userTrainingId: string) =>
    apiPost<any>(`/gamification/user-trainings/${userTrainingId}/create-checklist-instance`, {}),
  getUserQualifications: (userId?: string) => {
    const query = userId ? `?userId=${userId}` : '';
    return apiGet<UserQualification[]>(`/gamification/user-qualifications${query}`).then(
      (r: any) => (Array.isArray(r) ? r : (r.data ?? [])),
    );
  },
  awardQualification: (
    userId: string,
    qualificationId: string,
    awardedBy?: string,
    expiresAt?: string,
  ) =>
    apiPost<UserQualification>('/gamification/user-qualifications/award', {
      userId,
      qualificationId,
      awardedBy,
      expiresAt,
    }),
  revokeQualification: (userQualificationId: string) =>
    apiRequest<{ message: string }>(
      `/gamification/user-qualifications/${userQualificationId}/revoke`,
      {
        method: 'DELETE',
      },
    ),
  getPendingApprovals: () =>
    apiGet<UserTraining[]>('/gamification/user-trainings/pending-approval').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  approveTraining: (userTrainingId: string) =>
    apiPost<UserTraining>(`/gamification/user-trainings/${userTrainingId}/approve`, {}),
  rejectTraining: (userTrainingId: string, reason?: string) =>
    apiPost<UserTraining>(`/gamification/user-trainings/${userTrainingId}/reject`, { reason }),
  uploadCertificate: (data: {
    file: File;
    userTrainingId?: string;
    userQualificationId?: string;
    issuedBy?: string;
    issuedAt?: string;
    expiresAt?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.userTrainingId) formData.append('userTrainingId', data.userTrainingId);
    if (data.userQualificationId) formData.append('userQualificationId', data.userQualificationId);
    if (data.issuedBy) formData.append('issuedBy', data.issuedBy);
    if (data.issuedAt) formData.append('issuedAt', data.issuedAt);
    if (data.expiresAt) formData.append('expiresAt', data.expiresAt);
    return apiRequest<CertificateAttachment>('/gamification/certificate-attachments', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type with boundary
    });
  },
  getCertificateAttachments: (params?: {
    userTrainingId?: string;
    userQualificationId?: string;
    userId?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.userTrainingId) searchParams.append('userTrainingId', params.userTrainingId);
    if (params?.userQualificationId)
      searchParams.append('userQualificationId', params.userQualificationId);
    if (params?.userId) searchParams.append('userId', params.userId);
    const query = searchParams.toString();
    return apiGet<CertificateAttachment[]>(
      `/gamification/certificate-attachments${query ? `?${query}` : ''}`,
    ).then((r: any) => (Array.isArray(r) ? r : (r.data ?? [])));
  },
  getSkillCoverage: () =>
    apiGet<SkillCoverage[]>('/gamification/skill-coverage').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  getTrainingSettings: () => apiGet<TrainingSettings>('/gamification/training-settings'),
  updateTrainingSettings: (expiryWarningDays: number) =>
    apiPatch<TrainingSettings>('/gamification/training-settings', { expiryWarningDays }),
  listQualifications: () =>
    apiGet<Qualification[]>('/gamification/qualifications').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  createQualification: (data: Pick<Qualification, 'name' | 'description'>) =>
    apiPost<Qualification>('/gamification/qualifications', data),
  updateQualification: (id: string, data: Partial<Qualification>) =>
    apiPatch<Qualification>(`/gamification/qualifications/${id}`, data),
  listRules: () =>
    apiGet<RuleWithConditions[]>('/gamification/rules').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  createRule: (data: Partial<RuleDefinition>) =>
    apiPost<RuleDefinition>('/gamification/rules', data),
  updateRule: (id: string, data: Partial<RuleDefinition>) =>
    apiPatch<RuleDefinition>(`/gamification/rules/${id}`, data),
  testRule: (id: string, payload: Record<string, unknown>) =>
    apiPost<{ ok: boolean }>(`/gamification/rules/${id}/test`, payload),
  listBadges: () =>
    apiGet<BadgeDefinition[]>('/gamification/badges').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  createBadge: (data: Partial<BadgeDefinition>) =>
    apiPost<BadgeDefinition>('/gamification/badges', data),
  updateBadge: (id: string, data: Partial<BadgeDefinition>) =>
    apiPatch<BadgeDefinition>(`/gamification/badges/${id}`, data),
  listLevels: () =>
    apiGet<LevelDefinition[]>('/gamification/levels').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  createLevel: (data: Partial<LevelDefinition>) =>
    apiPost<LevelDefinition>('/gamification/levels', data),
  updateLevel: (id: string, data: Partial<LevelDefinition>) =>
    apiPatch<LevelDefinition>(`/gamification/levels/${id}`, data),
  addPoints: (payload: {
    userId: string;
    points: number;
    reason?: string;
    sourceType: string;
    sourceId?: string;
    metadata?: Record<string, unknown>;
  }) => apiPost<GamificationProfile['points']>('/gamification/points', payload),
  getPointsEvents: (userId: string, params?: { page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiGet<{
      data: PointsEvent[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/gamification/points-events/${userId}${query ? `?${query}` : ''}`);
  },

  // Competitions (micro-competitions)
  getCompetitions: () =>
    apiGet<Competition[]>('/gamification/competitions').then((r: any) =>
      Array.isArray(r) ? r : (r.data ?? []),
    ),
  getCompetition: (id: string) => apiGet<Competition>(`/gamification/competitions/${id}`),
  joinCompetition: (id: string) =>
    apiPost<{ ok: boolean }>(`/gamification/competitions/${id}/join`, {}),
  createCompetition: (data: Record<string, unknown>) =>
    apiPost<Competition>('/gamification/competitions', data),
  updateCompetition: (id: string, data: Partial<Competition>) =>
    apiPatch<Competition>(`/gamification/competitions/${id}`, data),
  deleteCompetition: (id: string) =>
    apiDelete<{ success: boolean }>(`/gamification/competitions/${id}`),

  // Competence profile
  getCompetenceProfile: (userId: string) =>
    apiGet<CompetenceProfile>(`/gamification/profile/${userId}/competence`),
};

// ===== KNOWLEDGE CENTER / KNOWLEDGE DOCS API =====
export const knowledgeDocsApi = {
  list: (params?: { page?: number; limit?: number; search?: string; folderId?: string | null }) => {
    const searchParams = new URLSearchParams();
    // Server defaults to page=1, limit=20 if not provided
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.folderId !== undefined && params.folderId !== null)
      searchParams.append('folderId', params.folderId);
    const query = searchParams.toString();
    // Server always returns paginated format: { data: [...], totalPages, totalItems }
    return apiGet<{ data: KnowledgeDoc[]; totalPages: number; totalItems: number }>(
      `/knowledge-docs${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<KnowledgeDoc>(`/knowledge-docs/${id}`),
  createRichText: (data: NewKnowledgeDoc) => apiPost<KnowledgeDoc>('/knowledge-docs', data),
  updateRichText: (id: string, data: NewKnowledgeDoc) =>
    apiPut<KnowledgeDoc>(`/knowledge-docs/${id}`, data),
  uploadFile: (payload: {
    file: File;
    title?: string;
    description?: string | null;
    folderId?: string | null;
  }) => {
    const formData = new FormData();
    formData.append('file', payload.file);
    if (payload.title) formData.append('title', payload.title);
    if (payload.description) formData.append('description', payload.description);
    if (payload.folderId) formData.append('folderId', payload.folderId);
    return apiRequest<KnowledgeDoc>('/knowledge-docs/upload', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest<{ url: string }>('/knowledge-docs/upload-image', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
  delete: (id: string) => apiDelete<void>(`/knowledge-docs/${id}`),
  moveDocument: (id: string, targetFolderId: string | null) =>
    apiPost<KnowledgeDoc>(`/knowledge-docs/${id}/move`, { targetFolderId }),
};

// ===== KNOWLEDGE FOLDERS API =====
export const knowledgeFoldersApi = {
  getTree: () => apiGet<KnowledgeFolderTreeNode[]>('/knowledge-folders/tree'),
  getById: (id: string) =>
    apiGet<KnowledgeFolder & { documents: KnowledgeDoc[]; childFolders: KnowledgeFolder[] }>(
      `/knowledge-folders/${id}`,
    ),
  create: (data: NewKnowledgeFolder) => apiPost<KnowledgeFolder>('/knowledge-folders', data),
  update: (id: string, data: Partial<NewKnowledgeFolder>) =>
    apiPut<KnowledgeFolder>(`/knowledge-folders/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/knowledge-folders/${id}`),
  move: (id: string, targetFolderId: string | null) =>
    apiPost<KnowledgeFolder>(`/knowledge-folders/${id}/move`, { targetFolderId }),
  reorder: (folderIds: string[]) =>
    apiPut<{ message: string }>('/knowledge-folders/reorder', { folderIds }),
};

// ===== PERMISSIONS API =====
export const permissionsApi = {
  getAll: () => apiGet<Permission[]>('/permissions'),
  getByCategory: () => apiGet<Record<string, Permission[]>>('/permissions/categories'),
  getByRole: (roleId: string) =>
    apiGet<{ data: Permission[]; totalPages?: number; totalItems?: number }>(
      `/permissions/role/${roleId}`,
    ),
  getByUser: (userId: string) =>
    apiGet<{ data: Permission[]; permissions: string[] }>(`/permissions/user/${userId}`),
  getMatrix: () =>
    apiGet<
      Array<{
        id: string;
        label: string;
        actions: Array<{ key: string; label: string; codes: string[] }>;
      }>
    >('/permissions/matrix'),
  assignToRole: (roleId: string, permissionIds: string[]) =>
    apiPost<Permission[]>(`/permissions/role/${roleId}`, { permissionIds }),
  addPermissionToRole: (roleId: string, permissionId: string) =>
    apiPost<Permission>(`/permissions/role/${roleId}/${permissionId}`, {}),
  removePermissionFromRole: (roleId: string, permissionId: string) =>
    apiDelete<Permission>(`/permissions/role/${roleId}/${permissionId}`),
  seed: () => apiPost<{ message: string; count: number }>('/permissions/seed', {}),
  create: (data: {
    code: string;
    name: string;
    description?: string | null;
    category?: string | null;
  }) => apiPost<Permission>('/permissions', data),
};

// ===== NOTIFICATIONS API =====
export type NotificationRule = {
  id: string;
  name: string;
  code: string;
  description: string | null;
  enabled: boolean;
  type: 'email' | 'push' | 'webhook';
  template: string | null;
  recipients: Array<{
    id: string;
    user: {
      id: string;
      name: string | null;
      email: string | null;
    };
  }>;
  createdAt: string;
  updatedAt: string;
};

export type NewNotificationRule = {
  name: string;
  code: string;
  description?: string | null;
  enabled?: boolean;
  type: 'email' | 'push' | 'webhook';
  template?: string | null;
};

export type NotificationLog = {
  id: string;
  ruleId: string;
  recipientId: string | null;
  recipientEmail: string | null;
  status: 'pending' | 'sent' | 'failed';
  error: string | null;
  data: string | null;
  sentAt: string | null;
  createdAt: string;
  rule: {
    id: string;
    name: string;
    code: string;
  } | null;
};

export type UserNotification = {
  id: string;
  userId: string;
  type: 'ticket' | 'checklist' | 'asset' | 'system';
  title: string;
  message: string;
  read: boolean;
  link: string | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UnreadCountResponse = {
  count: number;
};

export const notificationsApi = {
  getRules: () => apiGet<NotificationRule[]>('/notifications/rules'),
  getRuleById: (id: string) => apiGet<NotificationRule>(`/notifications/rules/${id}`),
  createRule: (data: NewNotificationRule) =>
    apiPost<NotificationRule>('/notifications/rules', data),
  updateRule: (id: string, data: Partial<NewNotificationRule>) =>
    apiPatch<NotificationRule>(`/notifications/rules/${id}`, data),
  deleteRule: (id: string) => apiDelete<void>(`/notifications/rules/${id}`),
  addRecipient: (ruleId: string, userId: string) =>
    apiPost<{ id: string }>(`/notifications/rules/${ruleId}/recipients`, { userId }),
  removeRecipient: (ruleId: string, userId: string) =>
    apiDelete<void>(`/notifications/rules/${ruleId}/recipients/${userId}`),
  getLogs: (params?: { ruleId?: string; status?: string; page?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.ruleId) searchParams.set('ruleId', params.ruleId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiGet<{
      data: NotificationLog[];
      totalPages: number;
      totalItems: number;
      page: number;
      limit: number;
    }>(`/notifications/logs${query ? `?${query}` : ''}`);
  },
  sendTest: (id: string) =>
    apiPost<{ message: string; sentTo: string }>(`/notifications/rules/${id}/test`, {}),
  // User notifications
  getNotifications: (params?: { unreadOnly?: boolean }) =>
    apiGet<UserNotification[]>(`/notifications${params?.unreadOnly ? '?unreadOnly=true' : ''}`),
  getUnreadCount: () => apiGet<UnreadCountResponse>('/notifications/unread-count'),
  markNotificationRead: (id: string) => apiPatch<UserNotification>(`/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => apiPost<{ success: boolean }>('/notifications/mark-all-read', {}),
};

// ===== COMMENTS API =====
export const commentsApi = {
  getAll: (params: { entityType: 'ticket' | 'asset' | 'knowledge_doc'; entityId: string }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('entityType', params.entityType);
    searchParams.set('entityId', params.entityId);
    const query = searchParams.toString();
    return apiGet<Comment[]>(`/comments?${query}`);
  },
  create: (data: NewComment) => apiPost<Comment>('/comments', data),
  update: (id: string, data: { content: string }) => apiPatch<Comment>(`/comments/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/comments/${id}`),
  // Reuse knowledge docs upload for comment images
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest<{ url: string }>('/knowledge-docs/upload-image', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },

  // Upload video attachment for comments
  uploadVideo: (file: File, duration: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('duration', String(duration));
    return apiRequest<{
      url: string;
      r2Key: string;
      attachmentId: string;
      duration: number;
      size: number;
    }>('/comments/video/upload', {
      method: 'POST',
      body: formData,
      headers: {},
    });
  },
};

// ===== SYSTEM SETTINGS API =====
export interface SystemSetting {
  id: string;
  domain: string;
  key: string;
  value: string;
  description: string | null;
  type: 'boolean' | 'string' | 'number' | 'json';
  defaultValue: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface SettingDefinition {
  domain: string;
  key: string;
  type: 'boolean' | 'string' | 'number' | 'json';
  defaultValue: string | number | boolean | object;
  description: string;
  label: string;
  options?: { value: string; label: string }[];
}

export type SettingDefinitions = Record<string, SettingDefinition[]>;

export const systemSettingsApi = {
  // Get all settings grouped by domain
  getAll: () => apiGet<SettingDefinitions>('/system-settings'),
  // Get settings for a specific domain
  getByDomain: (domain: string) => apiGet<SystemSetting[]>(`/system-settings/${domain}`),
  // Get setting definitions (metadata for UI generation)
  getDefinitions: () => apiGet<SettingDefinitions>('/system-settings/meta/definitions'),
  // Update a single setting value
  update: (domain: string, key: string, value: string | number | boolean) =>
    apiRequest<{ success: boolean; domain: string; key: string; value: string }>(
      `/system-settings/${domain}/${key}`,
      {
        method: 'PUT',
        body: JSON.stringify({ value }),
      },
    ),
};

// ===== PART PURCHASE REQUESTS API =====
export type PartPurchaseRequestStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface PartPurchaseRequestItem {
  id: string;
  requestId: string;
  partItemId: string;
  quantity: number;
  supplierId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  partItem?: {
    id: string;
    name: string;
    code: string;
    unitId: string | null;
    minStock: number | null;
    currentStock: number | null;
    supplierId: string | null;
  } | null;
  supplier?: {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    contactPerson: string | null;
    website: string | null;
  } | null;
}

export interface PartPurchaseRequest {
  id: string;
  requestNumber: string;
  requestedBy: string;
  status: PartPurchaseRequestStatus;
  notes: string | null;
  expectedDeliveryDate: string | null;
  createdAt: string;
  updatedAt: string;
  cancelledAt: string | null;
  cancelledBy: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  requestedByUser?: {
    id: string;
    name: string;
    email: string | null;
  } | null;
  cancelledByUser?: {
    id: string;
    name: string;
  } | null;
  items?: PartPurchaseRequestItem[];
}

export interface NewPartPurchaseRequest {
  status?: PartPurchaseRequestStatus;
  notes?: string;
  expectedDeliveryDate?: string;
  items: Array<{
    partItemId: string;
    quantity: number;
    supplierId?: string;
    notes?: string;
  }>;
}

export interface PurchaseRequestEmailConfig {
  id: string;
  enabled: boolean;
  ccSender: boolean;
  subjectTemplate?: string | null;
  recipients?: Array<{
    id: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
  }>;
}

export interface NewPurchaseRequestEmailConfig {
  enabled: boolean;
  ccSender: boolean;
}

export interface PartPurchaseRequestsResponse {
  data: PartPurchaseRequest[];
  totalPages: number;
  totalItems: number;
  page: number;
  limit: number;
}

export const partPurchaseRequestsApi = {
  getAll: (params?: { status?: string; page?: number; limit?: number; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    const query = searchParams.toString();
    return apiGet<PartPurchaseRequestsResponse>(
      `/part-purchase-requests${query ? `?${query}` : ''}`,
    );
  },
  getById: (id: string) => apiGet<PartPurchaseRequest>(`/part-purchase-requests/${id}`),
  create: (data: NewPartPurchaseRequest) =>
    apiPost<PartPurchaseRequest>('/part-purchase-requests', data),
  update: (
    id: string,
    data: Partial<NewPartPurchaseRequest & { status?: PartPurchaseRequestStatus }>,
  ) => apiPatch<PartPurchaseRequest>(`/part-purchase-requests/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/part-purchase-requests/${id}`),
  sendEmail: (id: string) =>
    apiPost<{ message: string }>(`/part-purchase-requests/${id}/send-email`, {}),
  // Email configuration endpoints
  getEmailConfig: () => apiGet<PurchaseRequestEmailConfig>('/part-purchase-requests/email-config'),
  updateEmailConfig: (data: NewPurchaseRequestEmailConfig) =>
    // Server uses PUT for create/update
    apiPut<PurchaseRequestEmailConfig>('/part-purchase-requests/email-config', data),
  getEmailRecipients: (configId: string) =>
    apiGet<PurchaseRequestEmailConfig['recipients']>(
      `/part-purchase-requests/email-config/${configId}/recipients`,
    ),
  addEmailRecipient: (configId: string, userId: string) =>
    apiPost<void>(`/part-purchase-requests/email-config/${configId}/recipients`, { userId }),
  removeEmailRecipient: (configId: string, userId: string) =>
    apiDelete<void>(`/part-purchase-requests/email-config/${configId}/recipients/${userId}`),
};

// ===== WEBHOOKS API =====
export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string; // JSON array of event types
  secret?: string; // Only returned when creating or rotating
  retryPolicy: {
    maxAttempts: number;
    initialDelay: number; // seconds
    multiplier: number;
  };
  retentionDays: number;
  isActive: number; // 0 or 1
  lastDeliveryAt: string | null;
  successRate: number;
  lastStatus: 'healthy' | 'degraded' | 'failing' | 'never-sent';
  createdAt: string;
  updatedAt: string;
}

export interface NewWebhook {
  name: string;
  url: string;
  events: string[];
  retryPolicy: {
    maxAttempts: number;
    initialDelay: number;
    multiplier: number;
  };
  retentionDays: number;
  isActive: boolean;
}

export interface WebhookDeliveryLog {
  id: string;
  webhookId: string;
  eventId: string;
  eventType: string;
  status: 'success' | 'failed' | 'pending';
  statusCode: number | null;
  duration: number | null;
  attemptNumber: number;
  response: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface WebhookEvent {
  id: string;
  name: string;
  category: 'ticket' | 'asset';
  description: string;
}

export const webhooksApi = {
  getAll: async () => {
    const webhooks = await apiGet<any[]>('/webhooks');
    return webhooks.map((w) => ({
      ...w,
      isActive: !!w.isActive,
      lastDeliveryAt: w.lastDeliveryAt ? new Date(w.lastDeliveryAt).toISOString() : null,
      retryPolicy: typeof w.retryPolicy === 'string' ? JSON.parse(w.retryPolicy) : w.retryPolicy,
    })) as Webhook[];
  },
  getById: async (id: string) => {
    const webhook = await apiGet<any>(`/webhooks/${id}`);
    return {
      ...webhook,
      isActive: !!webhook.isActive,
      lastDeliveryAt: webhook.lastDeliveryAt
        ? new Date(webhook.lastDeliveryAt).toISOString()
        : null,
      retryPolicy:
        typeof webhook.retryPolicy === 'string'
          ? JSON.parse(webhook.retryPolicy)
          : webhook.retryPolicy,
    } as Webhook;
  },
  create: (data: NewWebhook) => apiPost<Webhook & { secret: string }>('/webhooks', data),
  update: (id: string, data: Partial<NewWebhook>) => apiPatch<Webhook>(`/webhooks/${id}`, data),
  delete: (id: string) => apiDelete<{ message: string }>(`/webhooks/${id}`),
  test: (id: string) => apiPost<{ success: boolean; error?: string }>(`/webhooks/${id}/test`, {}),
  rotateSecret: (id: string) => apiPost<{ secret: string }>(`/webhooks/${id}/rotate-secret`, {}),
  getLogs: async (id: string) => {
    const logs = await apiGet<any[]>(`/webhooks/${id}/logs`);
    return logs.map((log) => ({
      id: log.id,
      webhookId: log.webhookId,
      eventId: log.eventId,
      eventType: log.eventType,
      status: log.success ? 'success' : log.success === false ? 'failed' : 'pending',
      statusCode: log.responseStatus,
      duration: log.duration,
      attemptNumber: log.attemptNumber,
      response: log.responseBody,
      errorMessage: log.error,
      createdAt: log.createdAt,
    })) as WebhookDeliveryLog[];
  },
  getEvents: () => apiGet<WebhookEvent[]>('/webhooks/events'),
};

// ===== API KEYS =====
export interface ApiKey {
  id: string;
  tenantId: string;
  name: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  isActive: number;
  createdAt: string;
  updatedAt: string;
}

export interface NewApiKey {
  name: string;
}

export interface ApiKeyWithKey extends ApiKey {
  apiKey: string;
}

export const apiKeysApi = {
  getAll: () => apiGet<ApiKey[]>('/api-keys'),
  create: (data: NewApiKey) => apiPost<ApiKeyWithKey>('/api-keys', data),
  delete: (id: string) => apiDelete<{ success: boolean }>(`/api-keys/${id}`),
  regenerate: (id: string) => apiPost<ApiKeyWithKey>(`/api-keys/${id}/regenerate`, {}),
};

// ============ CALIBRATIONS (Unified) ============

export type CalibrationApi = {
  id: string;
  entityType: 'tool' | 'part_batch';
  entityId: string;
  entityCode: string | null;
  entityName: string | null;
  calibratedAt: string;
  dueAt: string | null;
  result: 'pass' | 'fail';
  isExternal: boolean;
  notes: string | null;
  certificateFileId: string | null;
  certificateUrl: string | null;
  certificateFileName: string | null;
  createdByName: string | null;
  createdAt: string;
  // Supplier info
  supplierId: string | null;
  supplierName: string | null;
  // Result certificate metadata
  resultCertificateNumber: string | null;
  resultStandard: string | null;
  resultIssueDate: string | null;
  resultExpiryDate: string | null;
  // Checklist instance
  checklistInstanceId: string | null;
  checklistInstanceStatus: string | null;
};

export type CreateCalibrationRequest = {
  entityType: 'tool' | 'part_batch';
  entityId: string;
  calibratedAt: string;
  dueAt?: string | null;
  result: 'pass' | 'fail';
  isExternal: boolean;
  notes?: string | null;
  certificateFileId?: string | null;
  // Supplier selection
  supplierId?: string | null;
  // Result certificate metadata
  resultCertificateNumber?: string | null;
  resultStandard?: string | null;
  resultIssueDate?: string | null;
  resultExpiryDate?: string | null;
  // Checklist template
  checklistTemplateId?: string | null;
};

export type CalibrationCalendarEventApi = {
  id: string;
  title: string;
  start: string;
  end: string;
  color?: string;
  resource: {
    entityId: string;
    entityType: 'tool' | 'part_batch';
    entityName: string;
    entityCode: string;
    status: 'ok' | 'due_soon' | 'overdue' | 'never_calibrated';
    isExternal: boolean;
  };
};

export type CalibrationItemNeedingCalibrationApi = {
  id: string;
  entityCode: string;
  entityName: string;
  entityType: 'tool' | 'part_batch';
  status: 'ok' | 'due_soon' | 'overdue' | 'never_calibrated';
  lastCalibrationDate: string | null;
  nextDueDate: string | null;
  calibrationIntervalDays: number | null;
  isExternal: boolean | null;
};

export const calibrationsApi = {
  getAll: (params?: { entityType?: 'tool' | 'part_batch' }) => {
    const searchParams = new URLSearchParams();
    if (params?.entityType) {
      searchParams.set('entityType', params.entityType);
    }
    const query = searchParams.toString();
    return apiGet<{ data: CalibrationApi[]; totalPages?: number; totalItems?: number }>(
      `/calibrations${query ? `?${query}` : ''}`,
    );
  },

  getById: (id: string) => apiGet<CalibrationApi>(`/calibrations/${id}`),

  create: (data: CreateCalibrationRequest) => apiPost<CalibrationApi>('/calibrations', data),

  getToolHistory: (toolId: string) =>
    apiGet<{ data: CalibrationApi[] }>(`/calibrations/tools/${toolId}/history`),

  getPartBatchHistory: (batchId: string) =>
    apiGet<{ data: CalibrationApi[] }>(`/calibrations/part-batches/${batchId}/history`),

  getCalendarEvents: (params: {
    startDate: string;
    endDate: string;
    status?: 'all' | 'overdue' | 'due_soon' | 'ok' | 'never_calibrated';
    entityType?: 'all' | 'tool' | 'part_batch';
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    if (params.status) searchParams.set('status', params.status);
    if (params.entityType) searchParams.set('entityType', params.entityType);
    return apiGet<{ data: CalibrationCalendarEventApi[] }>(
      `/calibrations/calendar?${searchParams.toString()}`,
    );
  },

  getItemsNeedingCalibration: (params?: {
    status?: 'all' | 'overdue' | 'due_soon' | 'ok' | 'never_calibrated';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== 'all') {
      searchParams.set('status', params.status);
    }
    const query = searchParams.toString();
    return apiGet<{ data: CalibrationItemNeedingCalibrationApi[] }>(
      `/calibrations/items-needing-calibration${query ? `?${query}` : ''}`,
    );
  },

  getSupplierStats: () =>
    apiGet<{
      summary: {
        accreditedLabs: number;
        avgTurnaroundDays: number;
        firstPassYield: number;
      };
      suppliers: Array<{
        supplierId: string;
        supplierName: string;
        totalCalibrations: number;
        firstPassYield: number | null;
        avgTurnaroundDays: number | null;
        onTimeRate: number | null;
      }>;
    }>('/calibrations/supplier-stats'),

  createChecklistInstance: (id: string) =>
    apiPost<CalibrationApi>(`/calibrations/${id}/checklist`, {}),
};

export const teamsApi = {
  getAll: (params: {
    page?: number;
    limit?: number;
    search?: string;
    includeEffort?: boolean;
    prioritizeShift?: boolean;
    clientDate?: string;
    clientTime?: string;
    includeQualifications?: boolean;
    includeGroups?: boolean;
    includeGamification?: boolean;
    includeTickets?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.search) searchParams.set('search', params.search);
    if (params?.includeEffort) searchParams.set('includeEffort', 'true');
    if (params?.prioritizeShift) searchParams.set('prioritizeShift', 'true');
    if (params?.clientDate) searchParams.set('clientDate', params.clientDate);
    if (params?.clientTime) searchParams.set('clientTime', params.clientTime);
    if (params?.includeQualifications) searchParams.set('includeQualifications', 'true');
    if (params?.includeGroups) searchParams.set('includeGroups', 'true');
    if (params?.includeGamification) searchParams.set('includeGamification', 'true');
    if (params?.includeTickets) searchParams.set('includeTickets', 'true');
    const query = searchParams.toString();
    return apiGet<
      | User[]
      | {
          data: User[];
          pagination: { page: number; limit: number; totalItems: number; totalPages: number };
        }
    >(`/users${query ? `?${query}` : ''}`);
  },
};

// v1.50.0 — Inventory Optimization & Procurement API

export const warehousesApi = {
  getAll: () => apiGet<{ data: Warehouse[] }>('/warehouses').then((r) => (r as any).data ?? r),
  getById: (id: string) =>
    apiGet<{ data: Warehouse & { locations: WarehouseLocation[] } }>(`/warehouses/${id}`).then(
      (r) => (r as any).data ?? r,
    ),
  create: (data: NewWarehouse) =>
    apiPost<Warehouse>('/warehouses', data).then((r) => (r as any).data ?? r),
  update: (id: string, data: Partial<NewWarehouse>) =>
    apiPatch<Warehouse>(`/warehouses/${id}`, data).then((r) => (r as any).data ?? r),
  delete: (id: string) => apiDelete<void>(`/warehouses/${id}`),
  getLocations: (warehouseId: string) =>
    apiGet<{ data: WarehouseLocation[] }>(`/warehouses/${warehouseId}/locations`).then(
      (r) => (r as any).data ?? r,
    ),
  createLocation: (warehouseId: string, data: NewWarehouseLocation) =>
    apiPost<WarehouseLocation>(`/warehouses/${warehouseId}/locations`, data).then(
      (r) => (r as any).data ?? r,
    ),
  updateLocation: (warehouseId: string, locationId: string, data: Partial<NewWarehouseLocation>) =>
    apiPatch<WarehouseLocation>(`/warehouses/${warehouseId}/locations/${locationId}`, data).then(
      (r) => (r as any).data ?? r,
    ),
  deleteLocation: (warehouseId: string, locationId: string) =>
    apiDelete<void>(`/warehouses/${warehouseId}/locations/${locationId}`),
};

export const purchaseOrdersApi = {
  getAll: (params?: {
    status?: string;
    supplierId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return apiGet<{
      data: PurchaseOrder[];
      totalPages: number;
      totalItems: number;
      page: number;
      limit: number;
    }>(`/purchase-orders${query ? `?${query}` : ''}`);
  },
  getById: (id: string) =>
    apiGet<{ data: PurchaseOrder }>(`/purchase-orders/${id}`).then((r) => (r as any).data ?? r),
  create: (data: NewPurchaseOrder) =>
    apiPost<PurchaseOrder>('/purchase-orders', data).then((r) => (r as any).data ?? r),
  update: (id: string, data: Partial<PurchaseOrder & { status?: PurchaseOrderStatus }>) =>
    apiPatch<PurchaseOrder>(`/purchase-orders/${id}`, data).then((r) => (r as any).data ?? r),
  receive: (
    id: string,
    data: { items: Array<{ poItemId: string; quantity: number; warehouseLocationId?: string }> },
  ) =>
    apiPost<{ data: { status: PurchaseOrderStatus; allReceived: boolean } }>(
      `/purchase-orders/${id}/receive`,
      data,
    ),
  delete: (id: string) => apiDelete<{ message: string }>(`/purchase-orders/${id}`),
};

export const bomApi = {
  getForAsset: (assetId: string) =>
    apiGet<{ data: AssetBomItem[] }>(`/bom/asset/${assetId}`).then((r) => (r as any).data ?? r),
  getWhereUsed: (partItemId: string) =>
    apiGet<{ data: AssetBomItem[] }>(`/bom/part/${partItemId}`).then((r) => (r as any).data ?? r),
  create: (data: NewAssetBomItem) =>
    apiPost<AssetBomItem>('/bom', data).then((r) => (r as any).data ?? r),
  update: (id: string, data: Partial<NewAssetBomItem>) =>
    apiPatch<AssetBomItem>(`/bom/${id}`, data).then((r) => (r as any).data ?? r),
  delete: (id: string) => apiDelete<{ message: string }>(`/bom/${id}`),
};

export const inventoryOptimizationApi = {
  getDashboard: () =>
    apiGet<{ data: InventoryOptimizationDashboard }>('/inventory-optimization/dashboard').then(
      (r) => (r as any).data ?? r,
    ),
  getStocks: (params?: { warehouseId?: string; classification?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
    if (params?.classification) searchParams.set('classification', params.classification);
    const query = searchParams.toString();
    return apiGet<{ data: PartWarehouseStock[] }>(
      `/inventory-optimization/stocks${query ? `?${query}` : ''}`,
    ).then((r) => (r as any).data ?? r);
  },
  classify: () => apiPost('/inventory-optimization/classify', {}),
  calculateReorderPoints: (data?: { serviceLevel?: number; leadTimeDays?: number }) =>
    apiPost('/inventory-optimization/calculate-reorder-points', data ?? {}),
  updateStock: (id: string, data: Partial<PartWarehouseStock>) =>
    apiPatch<PartWarehouseStock>(`/inventory-optimization/stocks/${id}`, data).then(
      (r) => (r as any).data ?? r,
    ),
};

export const supplierPerformanceApi = {
  getDashboard: () =>
    apiGet<{ data: SupplierPerformanceDashboard }>('/supplier-performance/dashboard').then(
      (r) => (r as any).data ?? r,
    ),
  getForSupplier: (supplierId: string) =>
    apiGet<{ data: SupplierPerformanceMetric[] }>(
      `/supplier-performance/supplier/${supplierId}`,
    ).then((r) => (r as any).data ?? r),
  calculate: (data?: { periodStart?: string; periodEnd?: string }) =>
    apiPost('/supplier-performance/calculate', data ?? {}),
};

// v1.51.0 — Multi-Site & Enterprise Scale

export const sitesApi = {
  getAll: () => apiGet('/sites').then((r: any) => (r as any).data ?? r),
  getById: (id: string) => apiGet(`/sites/${id}`).then((r: any) => (r as any).data ?? r),
  create: (data: any) => apiPost('/sites', data).then((r: any) => (r as any).data ?? r),
  update: (id: string, data: any) =>
    apiPatch(`/sites/${id}`, data).then((r: any) => (r as any).data ?? r),
  delete: (id: string) => apiDelete(`/sites/${id}`),
  assignUser: (siteId: string, data: { userId: string; role?: string }) =>
    apiPost(`/sites/${siteId}/users`, data),
  removeUser: (siteId: string, userId: string) => apiDelete(`/sites/${siteId}/users/${userId}`),
  getMySites: () => apiGet('/sites/my-sites').then((r: any) => (r as any).data ?? r),
};

export const inventoryTransfersApi = {
  getAll: (params?: any) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.search) sp.set('search', params.search);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    const q = sp.toString();
    return apiGet(`/inventory-transfers${q ? `?${q}` : ''}`);
  },
  getById: (id: string) =>
    apiGet(`/inventory-transfers/${id}`).then((r: any) => (r as any).data ?? r),
  create: (data: any) => apiPost('/inventory-transfers', data),
  updateStatus: (id: string, status: string) =>
    apiPatch(`/inventory-transfers/${id}/status`, { status }),
  getSiteComparison: () => apiGet('/site-comparison').then((r: any) => (r as any).data ?? r),
};
